/**
 * Views/Prenomina/AgregarPrenomina.jsx
 *
 * Componente orquestador. Maneja:
 *   Paso 0 — Bandeja de nómina (delegada a BandejaPrenomina): la nómina que toca por
 *            cadencia, las recientes y un buscador con alcance. No navega sola.
 *   Paso 1 — Tabla de cálculo (delegada a TablaPrenomina).
 *   Paso 2 — Importar nómina histórica.
 *
 * Sub-componentes:
 *   BandejaPrenomina    — pantalla de entrada (paso 0)
 *   TablaPrenomina      — tabla principal Excel-like
 *   SobreRecibo         — modal balance general de un empleado
 *   ModalColumnas       — selector de columnas visibles
 *   ModalAgregarEmpleado — buscador de empleados
 */

import React, { Component } from 'react'
import { message, notification, PageHeader, Spin, Select, Steps, Tag, Modal, Alert, Descriptions, DatePicker, Collapse, Tooltip, Checkbox } from 'antd'
import axios from 'axios'
import { connect } from 'react-redux'
import { ButtonItem, SelectItem, ModalItem, TableItem, dineroDisabledMask, MensajeError } from '../../../Components/Items'
// Ya existía y se reusa tal cual: recibe un polizaId, pide polizas/datosPoliza,
// arma el PDF con el mismo polizaPdf.js de siempre y trae su propio botón de
// descarga. Lo usan también Libro Diario, Libro Mayor y Estado de Cuenta.
import ModalPdfPoliza from '../../../Components/ModalPdfPoliza'
import { LeftOutlined, ExclamationCircleOutlined, SafetyCertificateOutlined, DownloadOutlined, SyncOutlined, LockOutlined, UnlockOutlined, FileSearchOutlined, CloseCircleOutlined, HistoryOutlined, RollbackOutlined, DeleteOutlined, CalculatorOutlined } from '@ant-design/icons'
import IconBiwo from '../../../Icons/IconBiwo'
import moment from 'moment'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import polizaPdf from '../../../Pdf/polizaPdf'
import BandejaPrenomina     from './BandejaPrenomina/BandejaPrenomina'
import { ESTATUS_COLOR }    from './estatusPrenomina'
import { ESTILOS }          from './preNominaStyles'
import TablaPrenomina       from './TablaPrenomina/TablaPrenomina'
import SobreRecibo          from './SobreRecibo/SobreRecibo'
import ModalColumnas        from './ModalColumnas/ModalColumnas'
import PreferenciasNomina   from '../ConfiguracionNomina/PreferenciasNomina/PreferenciasNomina'
import ModalAgregarEmpleado from './ModalAgregarEmpleado/ModalAgregarEmpleado'
import ModalResetearLineas  from './ModalResetearLineas/ModalResetearLineas'
import ModalProgreso from '../../../Components/ModalProgreso'
import ModalReciboNomina   from './ModalReciboNomina/ModalReciboNomina'
import ModalVerRecibos     from './ModalVerRecibos/ModalVerRecibos'
import {
  filtrarPorFiniquito, sufijoFiniquito, etiquetaFiniquito, mensajeSinRecibos,
} from './filtroFiniquito'
import CancelarCfdiNomina  from './CancelarCfdiNomina/CancelarCfdiNomina'
import ImportarNominaHistorica from './NominaHistorica/ImportarNominaHistorica'
import ModalValidacionTimbrado from '../Empleados/ModalValidacionTimbrado/ModalValidacionTimbrado'
import SelectorFechaEmision, {
  fechaEmisionPorDefecto, errorFechaEmision, formatoFechaEmision,
} from '../../../Components/SelectorFechaEmision'
import ModalFormatoDescarga from '../../../Components/ModalFormatoDescarga'
import ModalEnviarRecibos   from '../../../Components/ModalEnviarRecibos'
import descargarRecibosMasivo from '../../../Components/DescargarRecibosMasivo'
import Compress from '../../../Components/Compress'

const { Option } = Select
const { Step }   = Steps

// Tamaño de tanda de la descarga masiva del XML de prueba. Cada tanda cuesta una
// carga completa del contexto de timbrado en el servidor, así que conviene que
// sea grande; el tope lo pone el peso de la respuesta (un XML de nómina ronda
// las decenas de KB).
const TAMANO_TANDA_XML_PRUEBA = 50

// Tandas de la descarga de "todos los recibos en un solo PDF". Cada recibo son
// ~15 consultas en el servidor, así que la tanda se mantiene chica aunque el
// render ya no corra allá; el backend rechaza más de 50 por petición.
const TAMANO_TANDA_RECIBOS_PDF = 25

// Cortinilla de entrada a la nómina. La entrada dura lo que el keyframe del logo
// (pnLogoEntra, 0.8s) y la salida lo que pnEntradaOut (0.45s) — ver preNominaStyles.js.
// Si se cambian allá, se cambian aquí: el JS sólo sabe cuándo quitar el nodo.
const DURACION_ANIM_ENTRADA = 850
const DURACION_ANIM_SALIDA  = 450

pdfMake.vfs = pdfFonts.pdfMake.vfs

// ─────────────────────────────────────────────────────────────────────────────
class AgregarPrenomina extends Component {

  state = {
    // UI
    loading:     false,
    calculando:  false,
    // 0 = bandeja de nómina   1 = prenómina activa   2 = importar nómina histórica
    paso:        0,

    // Catálogos (los del paso 1; el paso 0 carga los suyos)
    conceptosNomina:      [],
    todosEmpleados:       [],
    tiposIncidencias:     [],

    // Complemento "Timbrado de Prueba" de la empresa — llega en datosParaPrenomina.
    // Cuando está prendido, el timbrado va al ambiente de pruebas del PAC y toda
    // la UI de timbrar lo anuncia para que nadie crea que timbró de verdad.
    timbradoDePrueba: false,

    // Complemento "Timbrar Nómina Histórica" — llega por el mismo camino. Habilita el
    // botón por renglón para timbrar una línea de un periodo ya pagado en el sistema
    // anterior. Apagado por omisión: es una operación excepcional.
    timbrarNominaHistorica: false,
    timbrandoLineaHistorica: null,   // lineaId en curso, para el spinner del renglón
    enviandoTimbrado: false,         // confirmarCierre en vuelo (validación + POST), ver ahí

    // Paso 0 — la bandeja es un componente con estado propio. Subir su `key` lo
    // remonta, y eso es todo lo que hace falta para que vuelva fresca: no queda nada
    // aquí que se pueda olvidar de limpiar al regresar (era el bug de
    // `periodoSeleccionado`, que se quedaba armado con el periodo anterior).
    bandejaKey:           0,
    creandoPrenomina:     false,

    // Cortinilla de entrada a la nómina: null | 'entra' | 'sale'.
    animEntrada:          null,

    // Prenómina activa (paso 1)
    prenomina: null,

    // Modal sobre-recibo
    sobreReciboVisible:    false,
    empleadoIdSobreRecibo: null,

    // Modal columnas
    columnasVisible: false,

    // Modal de preferencias de nómina, abierto desde el engrane de la bandeja. Es la
    // misma pantalla de la pestaña de Configuración Nómina.
    preferenciasVisible: false,

    // Modal agregar empleado
    agregarEmpleadoVisible: false,

    // Modal resetear líneas (resincronizar contra el padrón de empleados activos)
    resetLineasVisible: false,
    resetResultado:     null,   // null | { quitadas, agregadas }

    // Progreso del cálculo (vía WebSocket). `empleadoActual` es el nombre de la
    // línea que el motor está calculando en este momento: con nóminas grandes es
    // lo que hace ver que avanza aunque el porcentaje tarde en moverse.
    progreso: { procesados: 0, total: null, empleadoActual: null },

    // Validación contra el SAT. Corre en background (una consulta al validador
    // por empleado) y reporta por el canal `cierre`, igual que el timbrado y la
    // cancelación: son los procesos largos de esta misma pantalla y nunca corren
    // a la par.
    validandoSat:       false,
    validacionProgreso: { procesados: 0, total: null, conProblemas: 0 },

    // Cierre / Timbrado
    cerrando:          false,
    cierreProgreso:    { procesados: 0, total: null, exitosos: 0, errores: 0 },
    cierreResultado:   null,   // null | { exitosos, errores, estatus }
    modalCierreVisible: false,

    // Modal CFDI (datosCfdiLinea)
    cfdiModal: { visible: false, loading: false, datos: null, lineaId: null, cfdis: [] },

    // Modal vista previa de póliza (previsualizarPoliza) — solo lectura, renderiza
    // el PDF real (polizaPdf.js) con marca de agua "VISTA PREVIA"
    polizaPreviewModal: { visible: false, loading: false, datos: null, empleadoIndex: 0 },

    // Pólizas contables REALES de una nómina ya timbrada. Con
    // configuracionNomina.polizaPorEmpleado hay una por empleado, así que primero
    // se muestra la lista; con una sola se abre su PDF directo.
    polizasNomina: { visible: false, loading: false, lista: [] },
    // polizaId que está viendo ModalPdfPoliza (null = cerrado).
    polizaPdfId: null,

    // Fecha de pago capturada en el modal de cierre
    fechaPagoModal: null,

    // Fecha de EMISIÓN del CFDI capturada en el modal de cierre. Es el atributo
    // `Fecha` del comprobante, no @FechaPago: el SAT la acepta hasta 72 h atrás
    // y eso permite timbrar un cierre que se corrió con días de retraso.
    fechaEmisionModal: null,

    // Modal de timbrado de una línea histórica. Era un Modal.confirm imperativo,
    // pero ahora tiene que sostener un DatePicker controlado.
    modalHistorica: { visible: false, linea: null, fecha: null },

    // Modal de confirmación del reintento de timbrado (encabezado y por renglón).
    // `lineaId: null` = reintentar todas las que quedaron en error.
    modalReintento: { visible: false, lineaId: null, fecha: null },

    // Validación de datos antes de timbrar. `desdeTimbrado` distingue el modal
    // que abrió la compuerta de confirmarCierre (ahí sí se ofrece "Timbrar de
    // todas formas") del que abrió el botón "Verificar Datos", que sólo informa.
    validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false },

    // Modal recibo de nómina PDF
    reciboModal: { visible: false, lineaId: null },
    // Guarda las líneas completas, no ids: el visor las necesita para poder
    // ordenar los recibos (por código, departamento, registro patronal...).
    // `filtroFiniquito` ('todos'|'sin'|'solo', ver filtroFiniquito.js) es la variante que se
    // eligió en el menú Recibos; aquí y en la descarga sólo sirve para el título y el archivo.
    verRecibosModal: { visible: false, lineas: [], filtroFiniquito: 'todos' },

    // Modal descarga masiva de recibos (XML/PDF/ambos) de la nómina activa
    descargaRecibosModal: { visible: false, loading: false, filtroFiniquito: 'todos' },
    enviarRecibosModal: { visible: false, lineaIds: [] },

    // Cancelación de CFDI ante el SAT. `lineaId: null` = cancelar la nómina
    // completa (todas las líneas timbradas); con lineaId = solo ese recibo.
    cancelarModal:        { visible: false, lineaId: null, nombreEmpleado: '' },
    cancelando:           false,
    cancelacionProgreso:  { procesados: 0, total: null, exitosos: 0, errores: 0 },
    cancelacionResultado: null,   // null | { exitosos, errores }

    // Nómina histórica — aplicar acumulados de un periodo ya pagado
    historicoModal:  { visible: false, generarPoliza: false },
    aplicandoHistorico: false,
  }

  componentDidMount() {
    this.consultarTodo()
    this._wsHandler = (event) => {
      const msg = event.detail
      if (msg.prenominaId !== this.state.prenomina?._id) return

      if (msg.evento === 'calculo_inicio') {
        this.setState({ calculando: true })
        this.iniciarRespaldo('calculo')
      }
      if (msg.evento === 'calculo_progreso') {
        this.setState({ progreso: { procesados: msg.procesados, total: msg.total, empleadoActual: msg.empleadoActual || null } })
      }
      if (msg.evento === 'calculo_fin') {
        this.pararRespaldo('calculo')
        this.setState({ calculando: false, progreso: { procesados: 0, total: null, empleadoActual: null } })
        this.cargarPrenomina(this.state.prenomina._id)
      }
      if (msg.evento === 'calculo_error') {
        this.pararRespaldo('calculo')
        this.setState({ calculando: false, progreso: { procesados: 0, total: null, empleadoActual: null } })
        message.error(msg.error || 'Error en el cálculo')
      }
    }
    window.addEventListener('calculo_nomina', this._wsHandler)

    this._cierreWsHandler = (event) => {
      const msg = event.detail
      if (msg.prenominaId !== this.state.prenomina?._id) return

      if (msg.evento === 'cierre_inicio') {
        this.setState({ cerrando: true, cierreResultado: null, cierreProgreso: { procesados: 0, total: msg.total, exitosos: 0, errores: 0 } })
        this.iniciarRespaldo('cierre')
      }
      if (msg.evento === 'cierre_progreso') {
        this.setState({ cierreProgreso: { procesados: msg.procesados, total: msg.total, exitosos: msg.exitosos, errores: msg.errores } })
      }
      if (msg.evento === 'cierre_fin') {
        this.pararRespaldo('cierre')
        // `sinTimbre` = recibos que salieron en ceros y no se mandaron al PAC. Sin este
        // dato la alerta diría "Nómina timbrada: N CFDIs" y no cuadraría con la lista
        // de empleados, sin explicar dónde quedaron los que faltan.
        this.setState({ cerrando: false, cierreResultado: { exitosos: msg.exitosos, errores: msg.errores, sinTimbre: msg.sinTimbre || 0, estatus: msg.estatus } })
        this.cargarPrenomina(this.state.prenomina._id)
      }
      if (msg.evento === 'cierre_error') {
        this.pararRespaldo('cierre')
        this.setState({ cerrando: false })
        message.error(msg.message || 'Error inesperado en el timbrado')
      }

      // La cancelación en lote viaja por este mismo canal (mismo proceso largo
      // de la misma pantalla, nunca corren a la par).
      if (msg.evento === 'cancelacion_inicio') {
        this.setState({ cancelando: true, cancelacionResultado: null, cancelacionProgreso: { procesados: 0, total: msg.total, exitosos: 0, errores: 0 } })
        this.iniciarRespaldo('cierre')
      }
      if (msg.evento === 'cancelacion_progreso') {
        this.setState({ cancelacionProgreso: { procesados: msg.procesados, total: msg.total, exitosos: msg.exitosos, errores: msg.errores } })
      }
      if (msg.evento === 'cancelacion_fin') {
        this.pararRespaldo('cierre')
        this.setState({
          cancelando: false,
          cancelacionResultado: { exitosos: msg.exitosos, errores: msg.errores },
        })
        if (msg.advertencias?.length) {
          notification.warning({
            message: 'Cancelación con advertencias',
            description: msg.advertencias.join(' '),
            duration: null,
          })
        }
        this.cargarPrenomina(this.state.prenomina._id)
      }
      if (msg.evento === 'cancelacion_error') {
        this.pararRespaldo('cierre')
        this.setState({ cancelando: false })
        message.error(msg.message || 'Error inesperado en la cancelación')
      }

      // La validación contra el SAT viaja por este mismo canal, por el mismo
      // motivo que la cancelación. El resultado NO viene en el payload —son
      // cientos de KB— sino que se recoge aparte con resultadoValidacion.
      if (msg.evento === 'validacion_progreso') {
        this.setState({ validacionProgreso: {
          procesados: msg.procesados, total: msg.total, conProblemas: msg.conProblemas || 0,
        } })
      }
      if (msg.evento === 'validacion_fin') {
        this.pararRespaldo('cierre')
        this.recogerResultadoValidacion(msg.resultadoDisponible !== false)
      }
      if (msg.evento === 'validacion_error') {
        this.pararRespaldo('cierre')
        this._recogiendoValidacion = false
        this.setState({
          validandoSat: false,
          validacion: { ...this.state.validacion, visible: true, loading: false },
        })
        message.error(msg.message || 'Error al validar la nómina contra el SAT')
      }
    }
    window.addEventListener('cierre_nomina', this._cierreWsHandler)
  }

  componentWillUnmount() {
    window.removeEventListener('calculo_nomina', this._wsHandler)
    window.removeEventListener('cierre_nomina', this._cierreWsHandler)
    this.pararRespaldo('calculo')
    this.pararRespaldo('cierre')
    this._desmontado = true
    this._animTimers.forEach(clearTimeout)
    this._animTimers = []
  }

  // ── Cortinilla de entrada a la nómina ──────────────────────────────────────
  // Se pide desde los dos caminos que entran —el botón del selector y una fila de
  // "recientes"—, así que es idempotente: el primero que llega la prende.
  _animTimers = []
  _animDesde = 0
  _desmontado = false

  // ── Preferencias de la empresa, tal como las reporta la bandeja ────────────
  // Las dos son campos de instancia y no `state` a propósito: ninguna tiene nada que
  // re-renderizar por sí misma, y sobre todo `_autoEntradaHecha` TIENE que sobrevivir al
  // remonte de la bandeja que hace volverABandeja() subiendo `bandejaKey`. Si el salto
  // automático viviera dentro de la bandeja, ese remonte lo dispararía otra vez y no
  // habría manera de regresar al selector.
  _autoEntradaHecha = false
  _vistaInicialTabla = 'importes'

  iniciarAnimEntrada() {
    if (this.state.animEntrada || this.state.paso === 1) return
    this._animDesde = Date.now()
    this.setState({ animEntrada: 'entra' })
  }

  // Se cierra cuando la prenómina ya está en pantalla, pero nunca antes de que el
  // logo termine de entrar: con una respuesta rápida, quitarla de inmediato sería un
  // parpadeo peor que no tenerla.
  terminarAnimEntrada() {
    if (!this.state.animEntrada) return
    const falta = Math.max(0, DURACION_ANIM_ENTRADA - (Date.now() - this._animDesde))
    this._animTimers.push(setTimeout(() => {
      if (this._desmontado) return
      this.setState({ animEntrada: 'sale' })
      this._animTimers.push(setTimeout(() => {
        if (!this._desmontado) this.setState({ animEntrada: null })
      }, DURACION_ANIM_SALIDA))
    }, falta))
  }

  // ── Respaldo cuando el WebSocket no entrega ────────────────────────────────
  // El progreso de cálculo y timbrado viaja por WebSocket, pero ese socket
  // puede no recibir nada: en producción hay dos servidores detrás del
  // balanceador y el trabajo puede correr en la instancia donde el navegador
  // NO tiene su socket, además de las caídas normales de red o suspensiones
  // del equipo. Sin este respaldo el spinner se queda girando para siempre.
  //
  // El backend mantiene una llave en Redis mientras el proceso vive
  // (Utils/estadoProceso.utils.js); aquí la consultamos y, si desapareció,
  // damos el proceso por terminado y recargamos la prenómina.

  _respaldos = { calculo: null, cierre: null }
  _respaldoSinEstado = { calculo: 0, cierre: 0 }
  // Prenóminas a las que ya se les preguntó por procesos vivos en este montaje.
  _rehidratado = new Set()
  // Candado del "recoger el resultado de la validación": el WebSocket y el
  // respaldo por polling pueden llegar los dos, y sería pedirlo dos veces.
  _recogiendoValidacion = false

  iniciarRespaldo(tipo) {
    this.pararRespaldo(tipo)
    this._respaldos[tipo] = setInterval(() => this.revisarRespaldo(tipo), 10000)
  }

  pararRespaldo(tipo) {
    if (this._respaldos[tipo]) clearInterval(this._respaldos[tipo])
    this._respaldos[tipo] = null
    this._respaldoSinEstado[tipo] = 0
  }

  async revisarRespaldo(tipo) {
    const prenominaId = this.state.prenomina?._id
    // Timbrado y cancelación comparten la llave 'cierre': son el mismo tipo de
    // proceso largo y nunca corren a la par, así que cualquiera de los dos
    // mantiene vivo este respaldo.
    const enCurso = tipo === 'calculo'
      ? this.state.calculando
      : (this.state.cerrando || this.state.cancelando || this.state.validandoSat)

    // El WebSocket ya cerró el proceso, o cambiamos de prenómina
    if (!prenominaId || !enCurso) return this.pararRespaldo(tipo)

    let res
    try {
      res = await axios.post(
        'prenomina/estadoProceso',
        { empresaId: this.props.empresaId, prenominaId, tipo },
        { headers: this.props.headersToken }
      )
    } catch { return }   // error de red: reintentamos en el siguiente tick

    if (res.data.success !== 2) return

    if (res.data.estado) {
      // Sigue vivo. Aprovechamos para sincronizar el avance, por si el
      // WebSocket tampoco está entregando los eventos de progreso.
      this._respaldoSinEstado[tipo] = 0
      const estado = res.data.estado
      if (tipo === 'calculo' && estado.evento === 'calculo_progreso') {
        this.setState({ progreso: {
          procesados: estado.procesados, total: estado.total, empleadoActual: estado.empleadoActual || null,
        } })
      }
      if (tipo === 'cierre' && estado.evento === 'cierre_progreso') {
        this.setState({ cierreProgreso: {
          procesados: estado.procesados, total: estado.total,
          exitosos: estado.exitosos, errores: estado.errores,
        } })
      }
      if (tipo === 'cierre' && estado.evento === 'cancelacion_progreso') {
        this.setState({ cancelacionProgreso: {
          procesados: estado.procesados, total: estado.total,
          exitosos: estado.exitosos, errores: estado.errores,
        } })
      }
      if (tipo === 'cierre' && estado.evento === 'validacion_progreso') {
        this.setState({ validacionProgreso: {
          procesados: estado.procesados, total: estado.total,
          conProblemas: estado.conProblemas || 0,
        } })
      }
      return
    }

    // Sin estado: el proceso terminó, o murió con el servidor. Exigimos dos
    // lecturas seguidas antes de actuar para que un Redis degradado no apague
    // el spinner a media corrida.
    this._respaldoSinEstado[tipo] += 1
    if (this._respaldoSinEstado[tipo] < 2) return

    this.pararRespaldo(tipo)

    // La validación no toca la prenómina: lo que hay que hacer al terminar es
    // recoger su resultado, no recargar. (Si el WebSocket sí entregó el
    // validacion_fin, el guard de recogerResultadoValidacion evita el doble.)
    if (tipo === 'cierre' && this.state.validandoSat) {
      await this.recogerResultadoValidacion(true)
      if (!this.state.cerrando && !this.state.cancelando) return
    }

    this.setState(tipo === 'calculo'
      ? { calculando: false, progreso: { procesados: 0, total: null, empleadoActual: null } }
      : { cerrando: false, cancelando: false })
    await this.cargarPrenomina(prenominaId)
  }

  // ── Rehidratar el avance al entrar a la pantalla ───────────────────────────
  // Si el usuario recarga el navegador a media corrida, los listeners se vuelven
  // a registrar pero `calculando`/`cerrando` arrancan en false: el modal no
  // aparecía y la pantalla quedaba usable sobre una nómina que se estaba
  // recalculando. Aquí se le pregunta a Redis si hay algo vivo.
  async rehidratarProcesos(prenominaId) {
    if (!prenominaId) return
    for (const tipo of ['calculo', 'cierre']) {
      let res
      try {
        res = await axios.post(
          'prenomina/estadoProceso',
          { empresaId: this.props.empresaId, prenominaId, tipo },
          { headers: this.props.headersToken }
        )
      } catch { continue }
      const estado = res?.data?.success === 2 ? res.data.estado : null
      if (!estado) continue

      if (tipo === 'calculo') {
        this.setState({
          calculando: true,
          progreso: {
            procesados: estado.procesados || 0,
            total: estado.total ?? null,
            empleadoActual: estado.empleadoActual || null,
          },
        })
        this.iniciarRespaldo('calculo')
      } else if (estado.evento === 'cancelacion_progreso') {
        this.setState({ cancelando: true, cancelacionProgreso: {
          procesados: estado.procesados || 0, total: estado.total ?? null,
          exitosos: estado.exitosos || 0, errores: estado.errores || 0,
        } })
        this.iniciarRespaldo('cierre')
      } else if (estado.evento === 'validacion_progreso') {
        this.setState({ validandoSat: true, validacionProgreso: {
          procesados: estado.procesados || 0, total: estado.total ?? null,
          conProblemas: estado.conProblemas || 0,
        } })
        this.iniciarRespaldo('cierre')
      } else {
        this.setState({ cerrando: true, cierreResultado: null, cierreProgreso: {
          procesados: estado.procesados || 0, total: estado.total ?? null,
          exitosos: estado.exitosos || 0, errores: estado.errores || 0,
        } })
        this.iniciarRespaldo('cierre')
      }
    }
  }

  atras() { this.props.history.goBack() }

  // ── Carga inicial ──────────────────────────────────────────────────────────
  async consultarTodo() {
    this.setState({ loading: true })
    try {
      const [resDatos, resTipos] = await Promise.all([
        axios.post(
          'prenomina/datosParaPrenomina',
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        ),
        axios.post(
          'tiposIncidencias/selectTodasTiposIncidencias',
          { empresaId: this.props.empresaId },
          { headers: this.props.headersToken }
        ),
      ])
      if (resDatos.data.success === 2) {
        this.setState({
          conceptosNomina:      resDatos.data.conceptosNomina      || [],
          todosEmpleados:       resDatos.data.empleados             || [],
          timbradoDePrueba:     !!resDatos.data.timbradoDePrueba,
          timbrarNominaHistorica: !!resDatos.data.timbrarNominaHistorica,
        })
      }
      if (resTipos.data.success === 2) {
        this.setState({ tiposIncidencias: resTipos.data.data || [] })
      }
    } catch { message.error('Error al cargar catálogos') }
    this.setState({ loading: false })
  }

  // ── Volver a la bandeja ────────────────────────────────────────────────────
  // Subir la key remonta BandejaPrenomina, que se recarga sola y arranca con su
  // estado en blanco. Es lo que evita que el paso 0 regrese con la selección rancia
  // del periodo anterior.
  volverABandeja() {
    this.setState(prev => ({ paso: 0, prenomina: null, bandejaKey: prev.bandejaKey + 1 }))
  }

  // ── Lo que la bandeja reporta al terminar su PRIMERA carga ─────────────────
  // Trae el periodo propuesto y las preferencias de la empresa. La decisión de saltar se
  // toma aquí y no en la bandeja porque `_autoEntradaHecha` tiene que sobrevivir a su
  // remonte (ver el comentario del campo).
  onDatosInicialesBandeja({ sugeridoId, preferencias } = {}) {
    this._vistaInicialTabla = preferencias?.vistaInicial === 'incidencias' ? 'incidencias' : 'importes'

    if (this._autoEntradaHecha) return
    // Se marca aunque NO se salte: "ya hubo una oportunidad de entrar directo en esta
    // visita". Al volver a la bandeja, se queda.
    this._autoEntradaHecha = true

    if (preferencias?.entrarDirecto && sugeridoId
      && this.state.paso === 0 && !this.state.creandoPrenomina) {
      this.abrirOCrearPrenomina(sugeridoId)
    }
  }

  // ── Abrir o crear la prenómina de un periodo ───────────────────────────────
  // Único camino de creación: por aquí pasan la acción primaria de la tarjeta, el
  // "también crear", el "ir al periodo de hoy" y los periodos libres del buscador.
  async abrirOCrearPrenomina(periodoId) {
    if (!periodoId || this.state.creandoPrenomina) return
    this.setState({ creandoPrenomina: true, loading: true })
    // Desde el clic, no desde cargarPrenomina: antes de ésta van una o dos llamadas
    // más, y sin la cortinilla ahí el botón se queda girando sin explicar nada.
    this.iniciarAnimEntrada()
    try {
      // Guarda 1, barata: si ya existe se abre y no se intenta crear.
      const ya = await axios.post(
        'prenomina/prenominaPorPeriodo',
        { empresaId: this.props.empresaId, periodoId },
        { headers: this.props.headersToken }
      )
      if (ya.data.success === 2 && ya.data.prenomina) {
        await this.cargarPrenomina(ya.data.prenomina._id)
        return
      }

      const res = await axios.post(
        'prenomina/agregarPrenomina',
        { empresaId: this.props.empresaId, periodoId },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        message.success('Prenómina creada')
        await this.cargarPrenomina(res.data.id)
      } else if (res.data.prenominaId) {
        // Guarda 2, la definitiva: el backend rechazó el duplicado y devuelve el id
        // de la que ya existía. Cierra la carrera que la guarda 1 no puede (dos
        // pestañas, o dos clics en el mismo instante). Antes este id se tiraba.
        message.info('Ya existía una prenómina para ese periodo — abriendo…')
        await this.cargarPrenomina(res.data.prenominaId)
      } else {
        message.error(res.data.message || 'Error al crear')
      }
    } catch { message.error('Error al crear la prenómina') }
    finally {
      this.setState({ creandoPrenomina: false, loading: false })
      // Si no se entró (error, o el backend no devolvió nada que abrir), la cortinilla
      // se cierra igual: quedarse puesta taparía la pantalla para siempre.
      if (this.state.paso !== 1) this.terminarAnimEntrada()
    }
  }

  // ── Nómina histórica ───────────────────────────────────────────────────────
  // Aplicar = escribir los acumulados del periodo ya pagado (y opcionalmente la
  // póliza). No timbra, no toca créditos ni el saldo de ajuste al neto — ver
  // erp_backend/Controllers/Prenomina/historica.controllers.js.
  async aplicarHistorico() {
    const { prenomina, historicoModal } = this.state
    if (!prenomina) return
    this.setState({ aplicandoHistorico: true })
    try {
      const res = await axios.post(
        'prenomina/aplicarNominaHistorica',
        {
          empresaId: this.props.empresaId,
          prenominaId: prenomina._id,
          generarPoliza: historicoModal.generarPoliza,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        message.success(res.data.message || 'Histórico aplicado')
        if (res.data.poliza?.error) {
          notification.warning({
            message: 'La póliza no se generó',
            description: res.data.poliza.error,
            duration: null,
          })
        } else if (res.data.poliza?.advertencias?.length) {
          notification.warning({
            message: 'Póliza generada con advertencias',
            description: res.data.poliza.advertencias.join(' '),
            duration: null,
          })
        }
        this.setState({ historicoModal: { visible: false, generarPoliza: false } })
        await this.cargarPrenomina(prenomina._id)
      } else {
        message.error(res.data.message || 'No se pudo aplicar el histórico')
      }
    } catch { message.error('Error al aplicar el histórico') }
    this.setState({ aplicandoHistorico: false })
  }

  revertirHistorico() {
    const { prenomina } = this.state
    if (!prenomina) return
    Modal.confirm({
      title: '¿Revertir el histórico?',
      icon: <ExclamationCircleOutlined />,
      width: 520,
      content: (
        <span>
          Se van a restar de los acumulados exactamente los importes que esta nómina sumó
          {prenomina.polizaHistoricaGenerada ? ', y la póliza va a recibir los renglones espejo' : ''}.
          Después vas a poder corregir los importes y volver a aplicarla.
        </span>
      ),
      okText: 'Revertir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        this.setState({ aplicandoHistorico: true })
        try {
          const res = await axios.post(
            'prenomina/revertirNominaHistorica',
            { empresaId: this.props.empresaId, prenominaId: prenomina._id },
            { headers: this.props.headersToken }
          )
          if (res.data.success === 2) {
            message.success(res.data.message || 'Histórico revertido')
            if (res.data.advertencias?.length) {
              notification.warning({
                message: 'Reversa con advertencias',
                description: res.data.advertencias.join(' '),
                duration: null,
              })
            }
            await this.cargarPrenomina(prenomina._id)
          } else {
            message.error(res.data.message || 'No se pudo revertir')
          }
        } catch { message.error('Error al revertir el histórico') }
        this.setState({ aplicandoHistorico: false })
      },
    })
  }

  eliminarHistorico() {
    const { prenomina } = this.state
    if (!prenomina) return
    Modal.confirm({
      title: '¿Eliminar esta nómina histórica?',
      icon: <ExclamationCircleOutlined />,
      content: 'Todavía no ha movido acumulados, así que se puede borrar sin dejar rastro y volver a importar el archivo.',
      okText: 'Eliminar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await axios.post(
            'prenomina/eliminarNominaHistorica',
            { empresaId: this.props.empresaId, prenominaId: prenomina._id },
            { headers: this.props.headersToken }
          )
          if (res.data.success === 2) {
            message.success('Nómina histórica eliminada')
            this.volverABandeja()
            this.consultarTodo()
          } else {
            message.error(res.data.message || 'No se pudo eliminar')
          }
        } catch { message.error('Error al eliminar la nómina histórica') }
      },
    })
  }

  /**
   * Timbrar UNA línea de la nómina histórica. Detrás del complemento
   * `timbrarNominaHistorica`; el botón sólo existe si está prendido.
   *
   * La confirmación es deliberadamente larga: esta nómina ya se pagó en el sistema
   * anterior y normalmente no se timbra, así que quien llega aquí tiene que saber
   * exactamente qué va a pasar y qué NO va a pasar.
   */
  timbrarLineaHistorica(linea) {
    const { prenomina } = this.state
    if (!prenomina || !linea) return
    // Ya no es un Modal.confirm: hace falta sostener el DatePicker de la fecha de
    // emisión como valor controlado, y un confirm imperativo no puede.
    this.setState({
      modalHistorica: { visible: true, linea, fecha: fechaEmisionPorDefecto() },
    })
  }

  async confirmarTimbrarLineaHistorica() {
    const { prenomina, modalHistorica } = this.state
    const linea = modalHistorica.linea
    if (!prenomina || !linea) return
    // Doble clic: el backend ya rechaza la segunda con el reclamo de la línea, pero
    // así ni sale la petición.
    if (this.state.timbrandoLineaHistorica) return

    const errorEmision = errorFechaEmision(modalHistorica.fecha)
    if (errorEmision) { message.error(errorEmision); return }

    this.setState({
      timbrandoLineaHistorica: linea._id,
      modalHistorica: { visible: false, linea: null, fecha: null },
    })
    try {
      const res = await axios.post(
        'prenomina/timbrarLineaHistorica',
        {
          empresaId:        this.props.empresaId,
          prenominaId:      prenomina._id,
          lineaId:          linea._id,
          fechaComprobante: formatoFechaEmision(modalHistorica.fecha),
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        message.success(res.data.message || 'Línea timbrada')
        await this.cargarPrenomina(prenomina._id)
      } else {
        // El rechazo del PAC llega interpretado —qué corregir y dónde— y el crudo del
        // SAT viaja aparte: el overlay lo esconde detrás de "ver detalle técnico" y
        // deja copiarlo, y no se cierra solo.
        MensajeError({
          message: res.data.errorTimbrado?.titulo || 'No se pudo timbrar la línea',
          description: res.data.errorTimbrado?.mensaje || res.data.message || 'Error al timbrar',
          detalleTecnico: res.data.errorTimbrado?.detalleTecnico,
        })
      }
    } catch { message.error('Error al timbrar la línea histórica') }
    this.setState({ timbrandoLineaHistorica: null })
  }

  // ── Cargar prenómina completa ──────────────────────────────────────────────
  async cargarPrenomina(id) {
    // Sólo al ENTRAR. Esta función también se llama al terminar un cálculo o un
    // timbrado, ya estando en el paso 1, y ahí una cortinilla sería un estorbo.
    this.iniciarAnimEntrada()

    // El estado de cierre/timbrado pertenece a la prenómina que se está mostrando
    // ahora; nunca debe sobrevivir a una recarga (evita que quede pegado el aviso
    // de timbrado de la prenómina anterior al cambiar a otra distinta).
    this.pararRespaldo('cierre')
    this.setState({
      cerrando:           false,
      cierreProgreso:     { procesados: 0, total: null, exitosos: 0, errores: 0 },
      cierreResultado:    null,
      modalCierreVisible: false,
      fechaPagoModal:     null,
      fechaEmisionModal:  null,
      modalHistorica:     { visible: false, linea: null, fecha: null },
      modalReintento:     { visible: false, lineaId: null, fecha: null },
    })

    const res = await axios.post(
      'prenomina/datosPrenomina',
      { empresaId: this.props.empresaId, prenominaId: id },
      { headers: this.props.headersToken }
    )
    if (res.data.success === 2) {
      const { success, ...prenomina } = res.data
      // `loading: false` va en el MISMO setState que el cambio de paso, no en el
      // `finally` del llamador: si no, el contenido del paso nuevo monta todavía bajo
      // el overlay del <Spin>, que lo difumina, y la animación de entrada se corre
      // completa detrás del velo sin que se alcance a ver.
      this.setState({ prenomina, paso: 1, loading: false })
      this.terminarAnimEntrada()

      // Sólo la primera vez que se abre esta prenómina en esta sesión de la
      // pantalla. `cargarPrenomina` también se llama al terminar un cálculo o un
      // timbrado, y ahí la llave de Redis se borra sin await: preguntar en ese
      // momento podría leer un avance ya viejo y volver a abrir el modal.
      if (!this._rehidratado.has(String(id))) {
        this._rehidratado.add(String(id))
        this.rehidratarProcesos(id)
      }
    } else {
      // No se pudo abrir: quitar la cortinilla o la pantalla se queda tapada.
      this.terminarAnimEntrada()
      message.error(res.data.message || 'No se pudo abrir la prenómina')
    }
  }

  // ── Calcular toda la prenómina ─────────────────────────────────────────────
  async calcularTodo() {
    const { prenomina } = this.state
    try {
      const res = await axios.post(
        'prenomina/calcularPrenomina',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ calculando: true, progreso: { procesados: 0, total: null, empleadoActual: null } })
        this.iniciarRespaldo('calculo')
      } else {
        message.error('Error al iniciar el cálculo')
      }
    } catch { message.error('Error al calcular') }
  }

  // ── Calcular una sola línea ────────────────────────────────────────────────
  async calcularLinea(empleadoId) {
    const { prenomina } = this.state
    try {
      const res = await axios.post(
        'prenomina/calcularPrenomina',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, soloEmpleadoId: empleadoId },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ calculando: true, progreso: { procesados: 0, total: null, empleadoActual: null } })
        this.iniciarRespaldo('calculo')
      }
    } catch { /* silencioso */ }
  }

  // ── Cola de capturas manuales del sobre-recibo ─────────────────────────────
  // Cambiar los días (valorManual) de una percepción recalcula la línea dentro de la misma
  // petición. Si la siguiente celda saliera antes de que termine ese recálculo, el backend la
  // rechazaría por "cálculo en curso" o el recálculo en vuelo le pisaría el valor. Por eso las
  // capturas salen una detrás de otra.
  encolarManual(tarea) {
    const siguiente = (this._colaManuales || Promise.resolve()).then(tarea, tarea)
    this._colaManuales = siguiente.catch(() => {})
    return siguiente
  }

  // Reemplaza la línea con la que regresó el backend. Se mezcla sobre la que ya estaba porque
  // `codigo`, `serieNumeroEmpleado`, `departamentoNombre`… no viven en la colección: los inyecta
  // datosPrenomina al cargar.
  aplicarLineaDeRespuesta(empleadoId, data) {
    this.setState((prev) => {
      const p = { ...prev.prenomina }
      const lineas = [...p.lineas]
      const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
      if (idx !== -1) lineas[idx] = { ...lineas[idx], ...data.linea }
      p.lineas = lineas
      Object.assign(p, data.totalesPrenomina)
      return { prenomina: p }
    })
  }

  // ── Editar importe manual ──────────────────────────────────────────────────
  editarImporte(empleadoId, conceptoNominaId, campo, valor) {
    return this.encolarManual(async () => {
      try {
        const res = await axios.post(
          'prenomina/editarImporteManual',
          {
            empresaId:      this.props.empresaId,
            prenominaId:    this.state.prenomina._id,
            empleadoId, conceptoNominaId, campo, valor,
          },
          { headers: this.props.headersToken }
        )
        if (res.data.success === 2) {
          this.aplicarLineaDeRespuesta(empleadoId, res.data)
          if (res.data.aviso) message.warning(res.data.aviso)
        } else {
          // Sin este mensaje la celda regresaba sola a su valor anterior y no se sabía por qué.
          message.error(res.data.message || 'No se pudo guardar')
        }
      } catch { message.error('Error al guardar') }
    })
  }

  // ── Agregar percepción/deducción manual ────────────────────────────────────
  async agregarConceptoManual(empleadoId, conceptoNominaId, importe) {
    try {
      const res = await axios.post(
        'prenomina/agregarConceptoManual',
        {
          empresaId:   this.props.empresaId,
          prenominaId: this.state.prenomina._id,
          empleadoId, conceptoNominaId, importe,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState((prev) => {
          const p = { ...prev.prenomina }
          const lineas = [...p.lineas]
          const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
          if (idx !== -1) lineas[idx] = res.data.linea
          p.lineas = lineas
          Object.assign(p, res.data.totalesPrenomina)
          return { prenomina: p }
        })
        return true
      }
      message.error(res.data.message || 'Error al agregar el concepto')
      return false
    } catch { message.error('Error al agregar el concepto'); return false }
  }

  // ── Quitar percepción/deducción manual ─────────────────────────────────────
  async quitarConceptoManual(empleadoId, conceptoNominaId) {
    try {
      const res = await axios.post(
        'prenomina/quitarConceptoManual',
        {
          empresaId:   this.props.empresaId,
          prenominaId: this.state.prenomina._id,
          empleadoId, conceptoNominaId,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState((prev) => {
          const p = { ...prev.prenomina }
          const lineas = [...p.lineas]
          const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
          if (idx !== -1) lineas[idx] = res.data.linea
          p.lineas = lineas
          Object.assign(p, res.data.totalesPrenomina)
          return { prenomina: p }
        })
      } else {
        message.error(res.data.message || 'Error al quitar el concepto')
      }
    } catch { message.error('Error al quitar el concepto') }
  }

  // ── Quitar el manual de UN concepto ────────────────────────────────────────
  // Si traía días capturados, el backend recalcula la línea; si sólo era un importe manual,
  // queda en 0 hasta recalcular, como siempre. Va por la misma cola que editarImporte.
  limpiarManualConcepto(empleadoId, conceptoNominaId) {
    return this.encolarManual(async () => {
      try {
        const res = await axios.post(
          'prenomina/limpiarManualConcepto',
          {
            empresaId:   this.props.empresaId,
            prenominaId: this.state.prenomina._id,
            empleadoId, conceptoNominaId,
          },
          { headers: this.props.headersToken }
        )
        if (res.data.success === 2) {
          this.aplicarLineaDeRespuesta(empleadoId, res.data)
          if (res.data.aviso) message.warning(res.data.aviso)
        } else {
          message.error(res.data.message || 'Error al quitar el manual')
        }
      } catch { message.error('Error al quitar el manual') }
    })
  }

  // ── Cálculo inverso a partir del neto deseado ──────────────────────────────
  async calcularNetoInverso(empleadoId, netoDeseado) {
    try {
      const res = await axios.post(
        'prenomina/calcularNetoInverso',
        {
          empresaId:   this.props.empresaId,
          prenominaId: this.state.prenomina._id,
          empleadoId, netoDeseado,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState((prev) => {
          const p = { ...prev.prenomina }
          const lineas = [...p.lineas]
          const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
          if (idx !== -1) lineas[idx] = res.data.linea
          p.lineas = lineas
          Object.assign(p, res.data.totalesPrenomina)
          return { prenomina: p }
        })
        return true
      }
      message.error(res.data.message || 'No fue posible ajustar al neto solicitado')
      return false
    } catch { message.error('No fue posible ajustar al neto solicitado'); return false }
  }

  // ── Deshacer cálculo de neto inverso ───────────────────────────────────────
  async revertirNetoInverso(empleadoId) {
    try {
      const res = await axios.post(
        'prenomina/revertirNetoInverso',
        {
          empresaId:   this.props.empresaId,
          prenominaId: this.state.prenomina._id,
          empleadoId,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState((prev) => {
          const p = { ...prev.prenomina }
          const lineas = [...p.lineas]
          const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
          if (idx !== -1) lineas[idx] = res.data.linea
          p.lineas = lineas
          Object.assign(p, res.data.totalesPrenomina)
          return { prenomina: p }
        })
        this.calcularLinea(empleadoId)
        return true
      }
      message.error(res.data.message || 'No se pudo deshacer el cálculo inverso')
      return false
    } catch { message.error('No se pudo deshacer el cálculo inverso'); return false }
  }

  // ── Quitar empleado ────────────────────────────────────────────────────────
  async quitarEmpleado(empleadoId) {
    const { Modal } = await import('antd')
    const { ExclamationCircleOutlined } = await import('@ant-design/icons')
    Modal.confirm({
      title:      '¿Quitar empleado de la prenómina?',
      icon:       <ExclamationCircleOutlined />,
      content:    'El empleado y todos sus cálculos serán removidos.',
      okText:     'Quitar',
      okType:     'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        const res = await axios.post(
          'prenomina/quitarEmpleadoPrenomina',
          { empresaId: this.props.empresaId, prenominaId: this.state.prenomina._id, empleadoId },
          { headers: this.props.headersToken }
        )
        if (res.data.success === 2) {
          this.setState((prev) => ({
            prenomina: {
              ...prev.prenomina,
              lineas: prev.prenomina.lineas.filter(l => l.empleadoId !== empleadoId),
            },
            sobreReciboVisible: prev.empleadoIdSobreRecibo === empleadoId ? false : prev.sobreReciboVisible,
          }))
        }
      },
    })
  }

  // ── Agregar empleado ───────────────────────────────────────────────────────
  async agregarEmpleado(empleadoId) {
    const res = await axios.post(
      'prenomina/agregarEmpleadoPrenomina',
      { empresaId: this.props.empresaId, prenominaId: this.state.prenomina._id, empleadoId },
      { headers: this.props.headersToken }
    )
    if (res.data.success === 2) {
      await this.cargarPrenomina(this.state.prenomina._id)
      message.success('Empleado agregado')
    } else {
      message.error(res.data.message || 'Error al agregar empleado')
    }
  }

  // ── Resetear líneas: qué hacer cuando el modal ya aplicó ───────────────────
  //
  // El refetch NO es opcional. `codigo`, `serieNumeroEmpleado`, `departamentoNombre` y
  // `proyectoNombre` no están en la colección: los inyecta `datosPrenomina` en memoria. Sin
  // recargar, el primer <td> de cada línea nueva sale vacío.
  async onResetAplicado({ quitadas, agregadas, calcular }) {
    await this.cargarPrenomina(this.state.prenomina._id)
    message.success(`Líneas resincronizadas: ${quitadas} quitada(s), ${agregadas} agregada(s)`)

    // Sólo hace falta recalcular si entraron líneas nuevas: nacen 'pendiente' con conceptos: []
    // y salen en ceros. Si sólo hubo bajas, el backend ya dejó los totales cuadrados y las líneas
    // que sobrevivieron no cambiaron.
    if (agregadas > 0) {
      if (calcular) this.calcularTodo()
      else this.setState({ resetResultado: { quitadas, agregadas } })
    }
  }

  // ── Columnas: guardar ──────────────────────────────────────────────────────
  async guardarColumnas(columnasVisibles) {
    const res = await axios.post(
      'prenomina/actualizarColumnasVisibles',
      { empresaId: this.props.empresaId, prenominaId: this.state.prenomina._id, columnasVisibles },
      { headers: this.props.headersToken }
    )
    if (res.data.success === 2) {
      this.setState({ prenomina: res.data.prenomina, columnasVisible: false })
    }
  }

  // ── Incidencias: guardar día ───────────────────────────────────────────────
  async guardarIncidenciaDia(empleadoId, diaPeriodo, fecha, entradas) {
    try {
      const res = await axios.post(
        'prenomina/guardarIncidenciaDia',
        {
          empresaId: this.props.empresaId,
          prenominaId: this.state.prenomina._id,
          empleadoId, diaPeriodo, fecha, entradas,
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        // Actualizar incidencias en la línea del empleado
        this.setState((prev) => {
          const p = { ...prev.prenomina }
          const lineas = [...p.lineas]
          const idx = lineas.findIndex(l => l.empleadoId === empleadoId)
          if (idx !== -1) {
            lineas[idx] = { ...lineas[idx], incidencias: res.data.incidencias }
          }
          p.lineas = lineas
          return { prenomina: p }
        })
      } else {
        // Sin esta rama el día capturado desaparecía sin decir nada: el POST contesta HTTP 200
        // con success: 0 (línea bloqueada, día fuera del periodo, día fuera de la relación
        // laboral, o choque de guardados concurrentes), así que el catch nunca se entera y el
        // usuario se quedaba viendo en la celda un valor que el servidor no guardó.
        message.error(res.data.message || 'No se pudo guardar la incidencia')
      }
    } catch { message.error('Error al guardar incidencia') }
  }

  // ── Incidencias: borrar todas las de la prenómina ──────────────────────────
  //
  // El aviso no es de trámite. incidencias[] es una proyección: las vacaciones viven en
  // MovimientoVacaciones, las incapacidades en Incapacidad y las permanentes en
  // IncidenciaPermanente, y el motor las vuelve a pintar en la siguiente corrida. Si el modal no
  // lo dijera, el usuario ve reaparecer días después de "borrar todo" y lo reporta como bug.
  async limpiarIncidencias() {
    const { prenomina } = this.state
    const { Modal } = await import('antd')
    const { ExclamationCircleOutlined } = await import('@ant-design/icons')
    Modal.confirm({
      title: '¿Borrar todas las incidencias de esta nómina?',
      icon: <ExclamationCircleOutlined />,
      width: 560,
      content: (
        <span>
          Se borran las incidencias de <b>todos los empleados</b> —faltas, retardos, horas extra y
          todo lo que se haya capturado a mano o importado de Excel o del reloj checador—. La
          rejilla queda en blanco para volver a empezar la captura.
          <br /><br />
          Las <b>vacaciones</b>, las <b>incapacidades</b> y las <b>incidencias permanentes</b> no se
          borran aquí: tienen su propio registro y el sistema las vuelve a poner al calcular.
        </span>
      ),
      okText: 'Borrar todo',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await axios.post(
            'prenomina/limpiarIncidenciasPrenomina',
            { empresaId: this.props.empresaId, prenominaId: prenomina._id },
            { headers: this.props.headersToken }
          )
          if (res.data.success === 2) {
            await this.cargarPrenomina(prenomina._id)
            const r = res.data.resumen || {}
            if (!r.lineasLimpiadas) {
              message.success('No había ninguna incidencia capturada en esta nómina.')
              return
            }
            const partes = [`Se borraron ${r.diasBorrados} día(s) en ${r.lineasLimpiadas} empleado(s).`]
            if (r.lineasOmitidasPorBloqueo > 0) {
              partes.push(`${r.lineasOmitidasPorBloqueo} empleado(s) ya timbrado(s) conservan las suyas.`)
            }
            message.success(`${partes.join(' ')} Presiona "Calcular todo" para reflejar los importes.`, 6)
          } else {
            message.error(res.data.message || 'No se pudieron borrar las incidencias')
          }
        } catch { message.error('Error al borrar las incidencias') }
      },
    })
  }

  // ── Incidencias: importar desde asistencia (reloj checador) ────────────────
  // `opciones.diasPeriodo`: posiciones del periodo a importar; sin él, el periodo completo.
  async importarIncidenciasAsistencia(opciones = {}) {
    const { prenomina } = this.state
    try {
      const res = await axios.post(
        'prenomina/importarIncidenciasAsistencia',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, diasPeriodo: opciones.diasPeriodo },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        await this.cargarPrenomina(prenomina._id)
        const r = res.data.resumen
        const partes = []
        if (r.diasElegidos != null) partes.push(`Se procesaron ${r.diasElegidos} de ${r.diasPeriodo} día(s) del periodo.`)
        partes.push(`${r.diasConIncidenciaImportados} día(s) importados en ${r.empleadosConIncidenciasImportadas} empleado(s).`)
        if (r.diasSeptimoLaborado > 0) partes.push(`${r.diasSeptimoLaborado} séptimo(s) día(s) laborado(s).`)
        if (r.diasSeptimoNoLaborado > 0) partes.push(`${r.diasSeptimoNoLaborado} séptimo(s) día(s) no laborado(s).`)
        if (r.diasRespetadosPorVacaciones > 0) partes.push(`${r.diasRespetadosPorVacaciones} día(s) de vacaciones se respetaron sin falta.`)
        if (r.diasLimpiadosPorPresente > 0) partes.push(`${r.diasLimpiadosPorPresente} día(s) se limpiaron por resultar "presente".`)
        if (r.diasOmitidosPorCapturaManual > 0) partes.push(`${r.diasOmitidosPorCapturaManual} día(s) se omitieron por captura manual.`)
        if (r.diasSinIncidenciaAplicable > 0) partes.push(`${r.diasSinIncidenciaAplicable} día(s) con incidencia detectada sin tipo configurado.`)
        if (r.empleadosOmitidosSinReloj > 0) {
          const nombres = (r.empleadosOmitidosSinRelojNombres || []).join(', ')
          const resto = r.empleadosOmitidosSinReloj - (r.empleadosOmitidosSinRelojNombres || []).length
          partes.push(
            `${r.empleadosOmitidosSinReloj} empleado(s) se omitieron por no tener asignacion vigente a un reloj checador${nombres ? `: ${nombres}` : ''}${resto > 0 ? ` y ${resto} mas` : ''}.`
          )
        }
        message.success(`${partes.join(' ')} Presiona "Calcular todo" para reflejar los importes.`, 8)
      } else {
        message.error(res.data.message || 'No se pudo importar la asistencia')
      }
    } catch { message.error('Error al importar incidencias de asistencia') }
  }

  // ── Incidencias: importar desde un Excel capturado por el usuario ───────────
  // Devuelve true/false para que el modal sepa si se puede cerrar.
  async importarIncidenciasExcel({ filas, vacaciones }) {
    const { prenomina } = this.state
    try {
      const res = await axios.post(
        'prenomina/importarIncidenciasExcel',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, filas, vacaciones },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        await this.cargarPrenomina(prenomina._id)
        const r = res.data.resumen
        const partes = []
        // Un archivo que sólo trae vacaciones no escribe ni un día de incidencia: ahí el
        // renglón de "0 día(s) importados" sobra y confunde.
        if (r.diasEscritos > 0 || r.movimientosVacacionesCreados === 0) {
          partes.push(`${r.diasEscritos} día(s) importados en ${r.empleadosProcesados} empleado(s).`)
        }
        if (r.movimientosVacacionesCreados > 0) {
          partes.push(`${r.movimientosVacacionesCreados} movimiento(s) de vacaciones creados (${r.diasVacacionesRegistrados} día(s)).`)
        }
        if (r.diasReemplazados > 0) partes.push(`${r.diasReemplazados} día(s) se reemplazaron.`)
        if (r.diasLimpiados > 0) partes.push(`${r.diasLimpiados} día(s) se limpiaron por venir vacíos.`)
        if (r.lineasOmitidasPorBloqueo > 0) partes.push(`${r.lineasOmitidasPorBloqueo} empleado(s) se omitieron por tener el recibo timbrado.`)
        message.success(`${partes.join(' ')} Presiona "Calcular todo" para reflejar los importes.`, 8)

        // Las vacaciones que no se pudieron registrar casi siempre son un traslape con un
        // movimiento que ya existía (reimportar el mismo archivo). No es un error del lote,
        // pero el usuario tiene que verlo o va a creer que se cargaron.
        if (r.vacacionesOmitidas?.length) {
          notification.warning({
            message: 'Vacaciones que no se registraron',
            description: (
              <div>
                {r.vacacionesOmitidas.map((v, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    <b>{v.nombre}</b>{v.rango ? ` (${v.rango})` : ''}: {v.mensaje}
                  </div>
                ))}
              </div>
            ),
            duration: null,
          })
        }
        if (r.clavesDesconocidas?.length) {
          message.warning(`Se ignoraron claves que no existen en el catálogo: ${r.clavesDesconocidas.join(', ')}`, 8)
        }
        return true
      }
      message.error(res.data.message || 'No se pudo importar el Excel de incidencias')
      return false
    } catch {
      message.error('Error al importar el Excel de incidencias')
      return false
    }
  }

  // ── Validación de datos antes de timbrar ────────────────────────────────────
  // Revisa, empleado por empleado, los campos que el CFDI de nómina necesita. Sin
  // esto los faltantes no se ven: el timbrado los tapa con defaults ('01' de tipo
  // de contrato, 'CMX' de entidad federativa, '1' de riesgo de puesto) y timbra
  // igual, o los descubre el PAC línea por línea cuando ya arrancó.
  // Devuelve los resultados para que confirmarCierre pueda decidir con ellos.
  async validarPrenomina({ incluirSat = false, abrirModal = true } = {}) {
    const { prenomina } = this.state
    if (!prenomina) return null

    // Cuando TODAS las líneas se rechazaron no quedan 'calculada'/'modificada' que
    // revisar, y el botón que se ve es "Reintentar Timbrado": ahí lo que hay que
    // validar son justo las que están en error.
    const listas = prenomina.lineas?.filter(l => l.estatus === 'calculada' || l.estatus === 'modificada') || []
    const soloErrores = listas.length === 0

    if (abrirModal) this.setState({ validacion: { visible: true, loading: true, resultados: [], desdeTimbrado: false } })
    try {
      const res = await axios.post(
        'prenomina/validarPrenominaParaTimbrado',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, incluirSat, soloErrores },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        if (abrirModal) {
          this.setState({ validacion: { visible: true, loading: false, resultados: res.data.resultados, desdeTimbrado: false } })
        }
        return res.data
      }
      if (abrirModal) this.setState({ validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false } })
      message.error(res.data.message || 'No se pudo verificar la nómina')
      return null
    } catch {
      if (abrirModal) this.setState({ validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false } })
      message.error('Error al verificar los datos de la nómina')
      return null
    }
  }

  // ── Validación contra el SAT (background) ───────────────────────────────────
  // A diferencia de la validación local, ésta consulta al validador del SAT una
  // vez POR EMPLEADO, secuencialmente y con freno. Corría dentro del request y
  // con 80+ empleados amarraba la petición hasta el final; ahora el servidor
  // responde de inmediato y el avance llega por el canal `cierre`.
  async validarPrenominaConSat() {
    const { prenomina } = this.state
    if (!prenomina) return

    const listas = prenomina.lineas?.filter(l => l.estatus === 'calculada' || l.estatus === 'modificada') || []
    const soloErrores = listas.length === 0

    // El modal de avance se abre desde el clic —en su estado "Preparando…"— y
    // el de resultados se queda ABIERTO debajo. Cerrarlo para volverlo a abrir al
    // terminar se veía como un parpadeo de tres transiciones para algo que es una
    // sola espera. El de avance lo tapa por z-index (ver ModalProgreso).
    this._recogiendoValidacion = false
    this.setState({
      validacion: { ...this.state.validacion, loading: true },
      validandoSat: true,
      validacionProgreso: { procesados: 0, total: null, conProblemas: 0 },
    })

    // Deja el modal de resultados como estaba y quita el de avance.
    const abortar = (texto) => {
      this.setState({ validandoSat: false, validacion: { ...this.state.validacion, loading: false } })
      message.error(texto)
    }

    try {
      const res = await axios.post(
        'prenomina/validarPrenominaParaTimbrado',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, incluirSat: true, soloErrores },
        { headers: this.props.headersToken }
      )
      if (res.data.success !== 2) {
        abortar(res.data.message || 'No se pudo verificar la nómina')
        return
      }

      // Sin líneas que validar el backend contesta de una vez, sin background.
      if (!res.data.enProgreso) {
        this.setState({
          validandoSat: false,
          validacion: {
            ...this.state.validacion, visible: true, loading: false, resultados: res.data.resultados || [],
          },
        })
        return
      }

      this.setState({ validacionProgreso: { procesados: 0, total: res.data.total, conProblemas: 0 } })
      this.iniciarRespaldo('cierre')
    } catch {
      abortar('Error al verificar los datos de la nómina')
    }
  }

  // El resultado no viaja en el evento —son cientos de KB con los errores de cada
  // empleado— sino que vive 15 min en Redis y se recoge aquí.
  async recogerResultadoValidacion(disponible) {
    if (this._recogiendoValidacion) return
    this._recogiendoValidacion = true
    this.setState({ validandoSat: false })

    const volverAlModal = (resultados) => this.setState({ validacion: {
      ...this.state.validacion, visible: true, loading: false,
      ...(resultados ? { resultados } : {}),
    } })

    if (!disponible) {
      volverAlModal(null)
      message.error('La validación terminó pero no se pudo recuperar el resultado. Vuelve a intentar.')
      return
    }

    try {
      const res = await axios.post(
        'prenomina/resultadoValidacion',
        { empresaId: this.props.empresaId, prenominaId: this.state.prenomina?._id },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        volverAlModal(res.data.resultados || [])
      } else {
        volverAlModal(null)
        message.error(res.data.message || 'No se pudo recuperar el resultado de la validación')
      }
    } catch {
      volverAlModal(null)
      message.error('Error al recuperar el resultado de la validación')
    }
  }

  // El XML que se le mandaría al PAC para esa línea, sin sellar: sirve para
  // compararlo contra el XML de un CFDI ya timbrado y ver qué cambió.
  async descargarXmlPrueba(item) {
    const { prenomina } = this.state
    try {
      const res = await axios.post(
        'prenomina/xmlPruebaLinea',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id, lineaId: item.lineaId },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        const blob = new Blob([res.data.xml], { type: 'application/xml' })
        const url  = URL.createObjectURL(blob)
        const a    = document.createElement('a')
        a.href     = url
        a.download = res.data.nombreArchivo || 'prueba.xml'
        a.click()
        URL.revokeObjectURL(url)
      } else {
        message.error(res.data.message || 'No se pudo generar el XML de prueba')
      }
    } catch { message.error('Error al descargar el XML de prueba') }
  }

  // Todos los XML de prueba en UN solo ZIP, no una descarga del navegador por
  // empleado. Las tandas son secuenciales a propósito: cada una recarga el
  // contexto de timbrado completo en el servidor, así que mandarlas en paralelo
  // sería un fan-out contra la base.
  async descargarTodosXmlPrueba() {
    const { prenomina } = this.state
    const lineaIds = this.state.validacion.resultados.map(r => r.lineaId)
    if (lineaIds.length === 0) return

    const archivos = []
    const usados   = new Set()
    message.loading({ content: `Generando ${lineaIds.length} XML...`, key: 'xmlPrueba', duration: 0 })

    try {
      for (let i = 0; i < lineaIds.length; i += TAMANO_TANDA_XML_PRUEBA) {
        const tanda = lineaIds.slice(i, i + TAMANO_TANDA_XML_PRUEBA)
        const res = await axios.post(
          'prenomina/xmlPruebaMasivo',
          { empresaId: this.props.empresaId, prenominaId: prenomina._id, lineaIds: tanda },
          { headers: this.props.headersToken }
        )
        if (res.data.success === 2) {
          for (const archivo of res.data.archivos) {
            if (!archivo.xml) continue
            // El nombre sale del serieNumero del empleado: si dos lo comparten,
            // JSZip se quedaría sólo con el último sin avisar.
            let nombre = archivo.nombreArchivo || `prueba_${archivo.lineaId}.xml`
            if (usados.has(nombre)) nombre = nombre.replace(/\.xml$/i, '') + `_${archivo.lineaId}.xml`
            usados.add(nombre)
            archivos.push({ nombre, contenido: archivo.xml })
          }
        }
        message.loading({
          content: `Generando XML... ${Math.min(i + tanda.length, lineaIds.length)} de ${lineaIds.length}`,
          key: 'xmlPrueba', duration: 0,
        })
      }
    } catch {
      message.error({ content: 'Error al generar los XML de prueba', key: 'xmlPrueba' })
      return
    }

    if (archivos.length === 0) {
      message.warning({ content: 'No se generó ningún XML de prueba', key: 'xmlPrueba' })
      return
    }

    Compress(archivos, `xml_prueba_${(prenomina.nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')}`)
    // Una línea puede quedar fuera si su payload truena; el ZIP se baja igual con
    // las demás, pero hay que decirlo o parece que están todas.
    const faltantes = lineaIds.length - archivos.length
    message.success({
      content: faltantes > 0
        ? `${archivos.length} de ${lineaIds.length} XML de prueba en un ZIP (${faltantes} no se pudieron generar)`
        : `${archivos.length} XML de prueba descargados en un ZIP`,
      key: 'xmlPrueba',
    })
  }

  // ── Timbrar nómina (ya cerrada) ─────────────────────────────────────────────
  // Candado contra el doble clic. La validación de abajo es una llamada de red y el modal
  // sigue abierto mientras corre, así que un segundo clic arrancaba OTRO timbrado de la
  // misma nómina (el backend ahora también lo rechaza, pero aquí ni sale la petición).
  // El flag de instancia es el que manda: setState es asíncrono y dos clics en el mismo
  // tick pasarían los dos; `enviandoTimbrado` sólo pinta el botón en loading.
  async confirmarCierre(forzar = false) {
    if (this._enviandoTimbrado || this.state.cerrando) return
    this._enviandoTimbrado = true
    this.setState({ enviandoTimbrado: true })
    try {
      await this._confirmarCierre(forzar)
    } finally {
      this._enviandoTimbrado = false
      this.setState({ enviandoTimbrado: false })
    }
  }

  async _confirmarCierre(forzar) {
    const { prenomina, fechaPagoModal, fechaEmisionModal } = this.state
    if (!fechaPagoModal) { message.error('Indica la fecha de pago antes de timbrar'); return }
    if (
      prenomina?.fechaInicio && prenomina?.fechaFin &&
      (fechaPagoModal.isBefore(moment(prenomina.fechaInicio), 'day') || fechaPagoModal.isAfter(moment(prenomina.fechaFin), 'day'))
    ) {
      message.error('La fecha de pago no se puede registrar: debe estar dentro de los días del periodo')
      return
    }

    // La fecha de emisión se revalida aquí y no sólo al elegirla: entre abrir el
    // modal y confirmar puede pasar mucho rato (sobre todo si el timbrado rebotó
    // al modal de validación y se vuelve por "Timbrar de todas formas"), y una
    // fecha que era buena envejece sola hasta salirse de la ventana del SAT.
    const errorEmision = errorFechaEmision(fechaEmisionModal)
    if (errorEmision) { message.error(errorEmision); return }

    // Compuerta de validación. Sólo la capa local (no consulta al SAT), que es
    // instantánea. Con problemas se abre el modal y NO se timbra; desde ahí se
    // puede forzar con "Timbrar de todas formas", porque el timbrado sí tolera
    // estos huecos y no le vamos a cerrar la puerta a quien sabe lo que hace.
    if (!forzar) {
      const datos = await this.validarPrenomina({ incluirSat: false, abrirModal: false })
      if (datos && datos.conProblemas > 0) {
        this.setState({
          modalCierreVisible: false,
          validacion: { visible: true, loading: false, resultados: datos.resultados, desdeTimbrado: true },
        })
        return
      }
    }

    this.setState({ modalCierreVisible: false, validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false } })
    try {
      const res = await axios.post(
        'prenomina/timbrarNomina',
        {
          empresaId: this.props.empresaId,
          prenominaId: prenomina._id,
          fechaPago: fechaPagoModal.format('YYYY-MM-DD'),
          fechaComprobante: formatoFechaEmision(fechaEmisionModal),
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ cerrando: true, cierreResultado: null, cierreProgreso: { procesados: 0, total: res.data.total, exitosos: 0, errores: 0 } })
        this.iniciarRespaldo('cierre')
      } else {
        message.error(res.data.message || 'No se puede timbrar la nómina')
      }
    } catch { message.error('Error al iniciar el timbrado') }
  }

  // ── Cerrar nómina (congela cálculos, no timbra) ─────────────────────────────
  cerrarNomina() {
    const { prenomina } = this.state
    Modal.confirm({
      title: '¿Cerrar esta nómina?',
      icon: <ExclamationCircleOutlined />,
      content: 'Ya no se podrá calcular, editar importes, agregar/quitar empleados ni tocar incidencias. Puedes reabrirla después si es necesario, mientras no se haya timbrado.',
      okText: 'Cerrar nómina',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await axios.post(
            'prenomina/cerrarNomina',
            { empresaId: this.props.empresaId, prenominaId: prenomina._id },
            { headers: this.props.headersToken }
          )
          if (res.data.success === 2) {
            message.success('Nómina cerrada')
            this.cargarPrenomina(prenomina._id)
          } else {
            message.error(res.data.message || 'No se pudo cerrar la nómina')
          }
        } catch { message.error('Error al cerrar la nómina') }
      },
    })
  }

  // ── Reabrir nómina cerrada ───────────────────────────────────────────────────
  reabrirNomina() {
    const { prenomina } = this.state
    Modal.confirm({
      title: '¿Reabrir esta nómina?',
      icon: <ExclamationCircleOutlined />,
      content: 'Se volverán a permitir cálculos y ediciones.',
      okText: 'Reabrir',
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          const res = await axios.post(
            'prenomina/reabrirNomina',
            { empresaId: this.props.empresaId, prenominaId: prenomina._id },
            { headers: this.props.headersToken }
          )
          if (res.data.success === 2) {
            message.success('Nómina reabierta')
            this.setState({ cierreResultado: null })
            this.cargarPrenomina(prenomina._id)
          } else {
            message.error(res.data.message || 'No se pudo reabrir la nómina')
          }
        } catch { message.error('Error al reabrir la nómina') }
      },
    })
  }

  // `lineaId` opcional: reintenta sólo esa línea (botón del renglón). Sin él,
  // todas las que quedaron en error. Abre el modal de confirmación porque el
  // reintento genera CFDIs nuevos y por tanto también pide fecha de emisión.
  abrirReintentoTimbrado(lineaId = null) {
    if (this.state.cerrando) return
    this.setState({
      modalReintento: { visible: true, lineaId: lineaId || null, fecha: fechaEmisionPorDefecto() },
    })
  }

  // El backend relee los datos del empleado, así que corregir el dato rechazado
  // y reintentar basta — no hay que reabrir ni recalcular la nómina.
  async reintentarTimbrado(lineaId = null, fechaEmision = null) {
    const { prenomina, cerrando } = this.state
    // `cerrando` se prende ANTES de la petición: ahora hay dos entradas al
    // reintento (el botón del encabezado y el de cada renglón) y dos corridas
    // en paralelo sobre la misma nómina se pisarían en la póliza.
    if (cerrando) return

    const errorEmision = errorFechaEmision(fechaEmision)
    if (errorEmision) { message.error(errorEmision); return }

    this.setState({
      cierreResultado: null,
      cerrando: true,
      cierreProgreso: { procesados: 0, total: null, exitosos: 0, errores: 0 },
      modalReintento: { visible: false, lineaId: null, fecha: null },
    })
    try {
      const res = await axios.post(
        'prenomina/reintentarTimbrado',
        {
          empresaId: this.props.empresaId,
          prenominaId: prenomina._id,
          fechaComprobante: formatoFechaEmision(fechaEmision),
          ...(lineaId ? { lineaId } : {}),
        },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ cierreProgreso: { procesados: 0, total: res.data.total, exitosos: 0, errores: 0 } })
        this.iniciarRespaldo('cierre')
      } else {
        this.setState({ cerrando: false })
        message.error(res.data.message || 'Error al reintentar timbrado')
      }
    } catch {
      this.setState({ cerrando: false })
      message.error('Error al reintentar timbrado')
    }
  }

  async abrirPreviaPoliza() {
    const { prenomina } = this.state
    this.setState({ polizaPreviewModal: { visible: true, loading: true, datos: null, empleadoIndex: 0 } })
    try {
      const res = await axios.post(
        'prenomina/previsualizarPoliza',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        this.setState({ polizaPreviewModal: { visible: true, loading: false, datos: res.data, empleadoIndex: 0 } })
        this.renderPreviaPoliza(res.data, 0)
      } else {
        this.setState({ polizaPreviewModal: { visible: false, loading: false, datos: null, empleadoIndex: 0 } })
        message.error(res.data.message || 'No se pudo obtener la vista previa de la póliza')
      }
    } catch {
      this.setState({ polizaPreviewModal: { visible: false, loading: false, datos: null, empleadoIndex: 0 } })
      message.error('Error al obtener la vista previa de la póliza')
    }
  }

  renderPreviaPoliza(datos, index) {
    const preview = datos?.previews?.[index]
    if (!preview) return
    const pdf = pdfMake.createPdf(polizaPdf({
      empresa: datos.empresa,
      poliza: preview.poliza,
      cargosabonos: preview.cargosabonos,
      documentos: [],
      detallada: true,
    }))
    pdf.open({}, window.frames['polizaPreviewFrame'])
  }

  cambiarEmpleadoPreviaPoliza(index) {
    const { polizaPreviewModal } = this.state
    this.setState({ polizaPreviewModal: { ...polizaPreviewModal, empleadoIndex: index } })
    this.renderPreviaPoliza(polizaPreviewModal.datos, index)
  }

  // ── Póliza contable REAL de una nómina ya timbrada ──────────────────────────
  // Distinta de la vista previa de arriba: aquélla RECALCULA lo que se generaría
  // y sólo existe antes de timbrar; ésta lee la Poliza que quedó guardada.
  //
  // Con `polizaPorEmpleado` hay una póliza por empleado, así que se abre la lista;
  // con una sola se salta la lista y se va directo al PDF.
  async abrirPolizaContable() {
    const { prenomina } = this.state
    this.setState({ polizasNomina: { visible: true, loading: true, lista: [] } })
    const cerrar = () => this.setState({ polizasNomina: { visible: false, loading: false, lista: [] } })
    try {
      const res = await axios.post(
        'polizas/polizaIdsPorPrenomina',
        { empresaId: this.props.empresaId, prenominaId: prenomina._id },
        { headers: this.props.headersToken }
      )
      if (res.data.success !== 2) {
        cerrar()
        message.warning(
          res.data.message ||
          'Esta nómina todavía no tiene póliza contable. Se genera al timbrar.'
        )
        return
      }
      const lista = res.data.polizas || []
      if (lista.length === 1) {
        this.setState({
          polizasNomina: { visible: false, loading: false, lista: [] },
          polizaPdfId: lista[0]._id,
        })
        return
      }
      this.setState({ polizasNomina: { visible: true, loading: false, lista } })
    } catch {
      cerrar()
      message.error('Error al consultar la póliza de la nómina')
    }
  }

  async abrirCfdi(linea) {
    this.setState({ cfdiModal: { visible: true, loading: true, datos: null, lineaId: linea._id } })
    try {
      const res = await axios.post(
        'prenomina/datosCfdiLinea',
        { empresaId: this.props.empresaId, lineaId: linea._id },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        // `cfdis` trae los dos comprobantes cuando el recibo se partió (finiquito con pagos por
        // separación): `datos` sigue siendo el principal para lo que ya lo leía.
        this.setState(prev => ({ cfdiModal: {
          ...prev.cfdiModal, loading: false, datos: res.data.data, cfdis: res.data.cfdis || [],
        } }))
      } else {
        this.setState(prev => ({ cfdiModal: { ...prev.cfdiModal, loading: false } }))
        message.error('Error al obtener datos del CFDI')
      }
    } catch {
      this.setState(prev => ({ cfdiModal: { ...prev.cfdiModal, loading: false } }))
      message.error('Error al obtener datos del CFDI')
    }
  }

  async descargarXml(lineaId) {
    try {
      const res = await axios.post(
        'prenomina/xmlCfdiLinea',
        { empresaId: this.props.empresaId, lineaId },
        { headers: this.props.headersToken }
      )
      if (res.data.success === 2) {
        // Un recibo de finiquito con pagos por separación tiene DOS XML —el ordinario y el de
        // régimen 13— y los dos son comprobantes distintos ante el SAT: se bajan ambos. El
        // nombre lo arma el backend igual que el del PDF, con su sufijo de división, así que
        // tampoco se pisan entre sí.
        const comprobantes = res.data.cfdis?.length
          ? res.data.cfdis
          : [{ uuid: res.data.uuid, xml: res.data.xml }]
        for (const c of comprobantes) {
          if (!c.xml) continue
          const blob = new Blob([c.xml], { type: 'application/xml' })
          const url  = URL.createObjectURL(blob)
          const a    = document.createElement('a')
          a.href     = url
          a.download = `${c.nombreBase || `${c.uuid}${c.division === 'separacion' ? '_SEPARACION' : ''}`}.xml`
          a.click()
          URL.revokeObjectURL(url)
        }
      } else {
        message.error('Error al obtener el XML')
      }
    } catch { message.error('Error al descargar XML') }
  }

  imprimirRecibo(lineaId) {
    this.setState({ reciboModal: { visible: true, lineaId } })
  }

  /**
   * Hallazgos de la validación local por línea, para enriquecer el reporte de no
   * timbrados con el POR QUÉ de los que ni siquiera se intentaron.
   *
   * Son DOS llamadas porque el endpoint filtra por estatus y ninguna sola cubre a
   * los dos grupos (validacionTimbrado.controllers.js): con `soloErrores: true`
   * valida las líneas en 'error_timbrado' y con `false` las 'calculada' y
   * 'modificada'. Van en paralelo.
   *
   * `incluirSat: false` a propósito: la consulta al SAT es secuencial y con
   * throttle, y haría inusable el reporte en una nómina grande.
   *
   * Nunca lanza: el reporte es útil aunque esto falle —cada línea se queda con el
   * motivo que da su estatus— así que un error aquí devuelve un mapa vacío.
   *
   * @returns {Promise<Map<string, {errores:Array, advertencias:Array}>>} por lineaId
   */
  async validacionParaReporte() {
    const { prenomina } = this.state
    if (!prenomina?._id) return new Map()

    const pedir = (soloErrores) => axios.post(
      'prenomina/validarPrenominaParaTimbrado',
      {
        empresaId: this.props.empresaId,
        prenominaId: prenomina._id,
        soloErrores,
        incluirSat: false,
      },
      { headers: this.props.headersToken }
    ).then(r => (r.data?.success === 2 ? (r.data.resultados || []) : []))
      .catch(() => [])

    try {
      const [conError, listas] = await Promise.all([pedir(true), pedir(false)])
      return new Map(
        [...conError, ...listas].map(r => [String(r.lineaId), r])
      )
    } catch {
      return new Map()
    }
  }

  // Una línea 'pendiente' no tiene conceptos calculados: su recibo saldría en
  // ceros. Todo lo demás sí tiene recibo, timbrado o no.
  lineasConRecibo() {
    return (this.state.prenomina?.lineas || []).filter(l => l.estatus !== 'pendiente')
  }

  // Las que tienen CFDI: su recibo es el definitivo, sin marca de vista previa.
  // Mismo criterio que usa la descarga de XML, que sin CFDI no tiene qué bajar.
  lineasTimbradas() {
    return (this.state.prenomina?.lineas || []).filter(l => l.estatus === 'timbrada')
  }

  // `filtroFiniquito` llega del submenú de Recibos cuando la prenómina trae finiquitos
  // ('sin' | 'solo' | 'todos'); sin finiquitos no llega y queda la nómina completa.
  abrirDescargaRecibos(filtroFiniquito = 'todos') {
    if (this.lineasConRecibo().length === 0) {
      message.info('No hay líneas calculadas con recibo para descargar en esta nómina')
      return
    }
    if (filtrarPorFiniquito(this.lineasConRecibo(), filtroFiniquito).length === 0) {
      message.info(mensajeSinRecibos(filtroFiniquito))
      return
    }
    this.setState({ descargaRecibosModal: { visible: true, loading: false, filtroFiniquito } })
  }

  /**
   * Visor de todos los recibos en un solo PDF para imprimirlos de una vez.
   *
   * Las líneas las manda TablaPrenomina ya filtradas por su buscador —el filtro
   * vive en ese componente—, así que aquí sólo se descartan las 'pendiente', que
   * no tienen conceptos calculados y saldrían en ceros.
   */
  abrirVerRecibos(lineas, filtroFiniquito = 'todos') {
    const calculadas = (lineas || []).filter(l => l.estatus !== 'pendiente')
    if (calculadas.length === 0) {
      message.info('No hay líneas calculadas con recibo que mostrar')
      return
    }
    // La variante del submenú (ordinaria / sólo finiquitos) se aplica aquí, encima del
    // buscador: el visor recibe ya sólo lo que se va a imprimir.
    const conRecibo = filtrarPorFiniquito(calculadas, filtroFiniquito)
    if (conRecibo.length === 0) {
      message.info(mensajeSinRecibos(filtroFiniquito))
      return
    }
    // Van las líneas completas: el visor deriva de ahí el subconjunto timbrado y
    // el conteo de sin timbrar, y necesita sus campos (código, departamento,
    // registro patronal, proyecto) para el selector de orden de impresión.
    this.setState({ verRecibosModal: { visible: true, lineas: conRecibo, filtroFiniquito } })
  }

  /**
   * Todos los recibos en UN archivo PDF, armado en el navegador.
   *
   * El servidor manda el árbol de contenido en JSON por tandas y aquí se
   * concatena y se renderiza: así el render de pdfmake —la mitad cara del
   * trabajo— corre en la máquina del usuario y no en el servidor, que es lo que
   * permite pasarse del techo de 50 recibos del visor.
   *
   * Las tandas van en serie, no en paralelo: cada una son ~15 consultas por
   * recibo del lado del servidor.
   */
  async descargarRecibosUnPdf(lineaIds, sufijo = '') {
    const { prenomina } = this.state
    const contenido = []
    let logo = null
    let docBase = null
    let watermark = null
    let todosSinTimbrar = true

    message.loading({ content: `Generando ${lineaIds.length} recibos...`, key: 'unPdf', duration: 0 })

    try {
      for (let i = 0; i < lineaIds.length; i += TAMANO_TANDA_RECIBOS_PDF) {
        const tanda = lineaIds.slice(i, i + TAMANO_TANDA_RECIBOS_PDF)
        const res = await axios.post(
          'prenomina/recibosNominaPartesPdf',
          {
            empresaId: this.props.empresaId,
            lineaIds: tanda,
            // Sin esto, el primer recibo de la segunda tanda se pegaría al último
            // de la primera en vez de empezar en página nueva.
            continuacion: contenido.length > 0,
          },
          { headers: this.props.headersToken }
        )
        if (res.data.success !== 2) continue

        contenido.push(...res.data.contenido)
        logo = logo || res.data.logo
        docBase = docBase || res.data.docBase
        watermark = watermark || res.data.watermarkSinTimbrar
        todosSinTimbrar = todosSinTimbrar && res.data.todosSinTimbrar

        message.loading({
          content: `Generando recibos... ${Math.min(i + tanda.length, lineaIds.length)} de ${lineaIds.length}`,
          key: 'unPdf', duration: 0,
        })
      }
    } catch {
      message.error({ content: 'Error al generar los recibos', key: 'unPdf' })
      return
    }

    if (contenido.length === 0 || !docBase) {
      message.warning({ content: 'No se pudo generar ningún recibo', key: 'unPdf' })
      return
    }

    message.loading({ content: 'Armando el PDF...', key: 'unPdf', duration: 0 })
    const nombre = `Recibos_${(prenomina.nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')}${sufijo}.pdf`

    // El watermark del documento sólo va si TODA la selección está sin timbrar;
    // los recibos sueltos sin timbrar ya traen su propia banda en el contenido.
    pdfMake.createPdf({
      ...(todosSinTimbrar && watermark ? { watermark } : {}),
      ...docBase,
      content: contenido,
      images: logo ? { logo } : {},
    }).download(nombre)

    message.success({ content: `${nombre} descargado`, key: 'unPdf' })
  }

  async confirmarDescargaRecibos(formato, soloTimbrados = false) {
    const { prenomina } = this.state
    const { filtroFiniquito } = this.state.descargaRecibosModal
    // El PDF se genera aunque la línea no esté timbrada (sale marcada como vista
    // previa), pero el XML solo existe si hay CFDI: esa descarga se filtra sola.
    // Encima va la variante del submenú (ordinaria / sólo finiquitos).
    const lineaIds = filtrarPorFiniquito(
      (formato === 'xml' || soloTimbrados) ? this.lineasTimbradas() : this.lineasConRecibo(),
      filtroFiniquito
    ).map(l => l._id)

    if (lineaIds.length === 0) {
      message.info(
        formato === 'xml' || soloTimbrados
          ? 'No hay recibos timbrados en esta nómina'
          : 'No hay recibos que descargar en esta nómina'
      )
      this.setState({ descargaRecibosModal: { visible: false, loading: false } })
      return
    }

    this.setState(prev => ({ descargaRecibosModal: { ...prev.descargaRecibosModal, loading: true } }))

    // Los filtros van en el nombre del archivo: si no, la descarga completa, la de
    // solo timbrados y la de sólo finiquitos de la misma nómina salen con el mismo nombre.
    const sufijo = sufijoFiniquito(filtroFiniquito) + (soloTimbrados ? '_timbrados' : '')

    // Un solo PDF no pasa por descargarRecibosMasivo: ése arma un ZIP de archivos
    // sueltos y aquí el resultado es un único documento.
    if (formato === 'unPdf') {
      await this.descargarRecibosUnPdf(lineaIds, sufijo)
      this.setState({ descargaRecibosModal: { visible: false, loading: false } })
      return
    }

    await descargarRecibosMasivo({
      empresaId: this.props.empresaId,
      headersToken: this.props.headersToken,
      lineaIds,
      formato,
      nombreZip: `recibos_${(prenomina.nombre || 'nomina').replace(/[/\\?*[\]:#]/g, '_')}${sufijo}`,
    })
    this.setState({ descargaRecibosModal: { visible: false, loading: false } })
  }

  abrirEnvioRecibos(filtroFiniquito = 'todos') {
    if (this.lineasConRecibo().length === 0) {
      message.info('No hay líneas calculadas con recibo para enviar en esta nómina')
      return
    }
    const lineaIds = filtrarPorFiniquito(this.lineasConRecibo(), filtroFiniquito).map(l => l._id)
    if (lineaIds.length === 0) {
      message.info(mensajeSinRecibos(filtroFiniquito))
      return
    }
    this.setState({ enviarRecibosModal: { visible: true, lineaIds } })
  }

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER PRINCIPAL
  // ─────────────────────────────────────────────────────────────────────────
  render() {
    const {
      paso, loading, calculando, progreso, prenomina, animEntrada,
      conceptosNomina, todosEmpleados, tiposIncidencias,
      sobreReciboVisible, empleadoIdSobreRecibo,
      columnasVisible, agregarEmpleadoVisible, resetLineasVisible, resetResultado,
      cerrando, cierreProgreso, cierreResultado, modalCierreVisible,
      cfdiModal, polizaPreviewModal, fechaPagoModal, reciboModal, verRecibosModal, descargaRecibosModal, enviarRecibosModal,
      timbradoDePrueba, cancelarModal, cancelando, cancelacionProgreso, cancelacionResultado,
      validandoSat, validacionProgreso,
      historicoModal, aplicandoHistorico,
      fechaEmisionModal, modalHistorica, modalReintento,
    } = this.state

    const lineaSobreRecibo = prenomina?.lineas?.find(l => l.empleadoId === empleadoIdSobreRecibo) || null

    // Además del estatus de la nómina, la línea manda: al reabrir una nómina
    // parcialmente timbrada conviven líneas editables con líneas ya timbradas,
    // que son inmutables aunque la nómina esté abierta. Una línea 'historica' es
    // inmutable por el mismo motivo: sus acumulados ya se aplicaron.
    const soloLecturaSobreRecibo =
      ['timbrada', 'cerrada', 'parcialmente_timbrada', 'historica'].includes(prenomina?.estatus) ||
      ['timbrada', 'historica'].includes(lineaSobreRecibo?.estatus)

    // Datos para el modal de cierre
    const lineasListas  = prenomina?.lineas?.filter(l => l.estatus === 'calculada' || l.estatus === 'modificada') || []
    const lineasConError = prenomina?.lineas?.filter(l => l.estatus === 'error_timbrado') || []
    const lineasTimbradas = prenomina?.lineas?.filter(l => l.estatus === 'timbrada') || []
    const noSumaIds     = new Set((conceptosNomina || []).filter(c => c.noSuma).map(c => c._id?.toString()))
    // Percepciones en especie: fuera del neto, porque no se le entregan en dinero al
    // empleado. Este neto es el que se enseña en el modal de cierre como "lo que se paga".
    const especieIds    = new Set((conceptosNomina || []).filter(c => c.tipo === 1 && c.especie && !c.noSuma).map(c => c._id?.toString()))
    const importeEfectivo = (c) => c.importeTotalManual ?? (c.importeTotal || 0)
    const fueraDeTotales = (c) => noSumaIds.has(c.conceptoNominaId?.toString()) || c.noSuma
    const esEnEspecie = (c) => especieIds.has(c.conceptoNominaId?.toString()) || c.especie
    const netoLinea = (l) => {
      const perc = (l.conceptos || []).filter(c => c.tipo === 1 && !fueraDeTotales(c) && !esEnEspecie(c)).reduce((s, c) => s + importeEfectivo(c), 0)
      const dedu = (l.conceptos || []).filter(c => c.tipo === 2 && !fueraDeTotales(c)).reduce((s, c) => s + importeEfectivo(c), 0)
      return perc - dedu
    }
    const netoTotalListas = lineasListas.reduce((s, l) => s + netoLinea(l), 0)

    // ── Nómina histórica ───────────────────────────────────────────────────
    // Una histórica nunca se cierra ni se timbra: su ciclo es
    // importar → Calcular → Aplicar histórico → (Revertir histórico).
    const esHistorica    = paso === 1 && !!prenomina?.esHistorica
    const historicaAplicada = esHistorica && prenomina?.estatus === 'historica'
    const puedeAplicarHistorico  = esHistorica && !historicaAplicada
      && !aplicandoHistorico && !calculando && prenomina?.estatus === 'calculada'
    const puedeRevertirHistorico = historicaAplicada && !aplicandoHistorico
    const puedeEliminarHistorica = esHistorica && !historicaAplicada && !aplicandoHistorico && !calculando

    const puedeCerrar  = !esHistorica && paso === 1 && !cerrando && !cancelando && prenomina?.estatus === 'calculada' && lineasListas.length > 0
    // 'parcialmente_timbrada' entra en reabrir/timbrar porque una línea cuyo CFDI
    // se canceló regresa a 'calculada' dentro de una nómina que sigue teniendo
    // otras líneas timbradas: hay que poder corregirla y volver a timbrarla.
    const puedeReabrir = !esHistorica && paso === 1 && !cerrando && !cancelando
      && ['cerrada', 'parcialmente_timbrada'].includes(prenomina?.estatus)
    const puedeTimbre  = !esHistorica && paso === 1 && !cerrando && !cancelando && lineasListas.length > 0
      && ['cerrada', 'parcialmente_timbrada'].includes(prenomina?.estatus)
    const puedeReintentar = !esHistorica && paso === 1 && !cerrando && !cancelando && lineasConError.length > 0
      && ['cerrada', 'parcialmente_timbrada'].includes(prenomina?.estatus)
    const puedeCancelar = !esHistorica && paso === 1 && !cerrando && !cancelando && lineasTimbradas.length > 0

    // La vista previa RECALCULA lo que se generaría y sólo existe antes de
    // timbrar; la póliza contable es la que ya quedó guardada. En una nómina
    // parcialmente timbrada las dos tienen sentido y salen las dos.
    const puedeVerPolizaContable = ['timbrada', 'parcialmente_timbrada'].includes(prenomina?.estatus)

    return (
      <>
        <style>{ESTILOS}</style>

        {/* Cortinilla de entrada a la nómina: tapa el salto del paso 0 al 1, que si no
            es un parpadeo seco. Versión mínima de la intro del wizard de Configuración
            de Nómina — el mismo logo y el mismo cian, en menos de un segundo. */}
        {animEntrada && (
          <div className={`pn-entrada${animEntrada === 'sale' ? ' pn-entrada-sale' : ''}`}>
            <span className="pn-entrada-anillo" />
            <span className="pn-entrada-logo">
              <IconBiwo size={92} marginRight={0} color="#00BFD6" />
            </span>
          </div>
        )}

        <PageHeader
          onBack={paso === 1 || paso === 2
            ? () => this.volverABandeja()
            : this.atras.bind(this)
          }
          title={
            paso === 1 ? <span style={{ fontSize: 14 }}>{prenomina?.nombre}</span> :
            paso === 2 ? 'Cargar nómina histórica' :
                         'Prenómina'
          }
          backIcon={paso === 1 || paso === 2 ? <LeftOutlined /> : undefined}
          style={{
            border: '1px solid rgb(235,237,240)', borderRadius: 10,
            backgroundColor: 'white', marginBottom: 10,
          }}
          extra={paso === 1 && [
            <Tag
              key="status"
              color={ESTATUS_COLOR[prenomina?.estatus] || 'default'}
              style={{ fontSize: 12 }}
            >
              {prenomina?.estatus?.toUpperCase()}
            </Tag>,
            timbradoDePrueba && (
              <Tooltip
                key="prueba"
                title="La empresa tiene prendido el complemento 'Timbrado de Prueba': los CFDIs se emiten en el ambiente de pruebas del PAC y no son válidos ante el SAT."
              >
                <Tag color="volcano" style={{ fontSize: 12, fontWeight: 600 }}>
                  PRUEBA
                </Tag>
              </Tooltip>
            ),
            puedeCerrar && (
              <ButtonItem
                key="cerrar-calculo"
                ambar
                icon={<LockOutlined />}
                onClick={() => this.cerrarNomina()}
              >
                Cerrar Nómina
              </ButtonItem>
            ),
            puedeReabrir && (
              <ButtonItem
                key="reabrir"
                azul
                icon={<UnlockOutlined />}
                onClick={() => this.reabrirNomina()}
              >
                Reabrir Nómina
              </ButtonItem>
            ),
            (puedeCerrar || puedeTimbre || puedeReintentar) && (
              <ButtonItem
                key="verificar-datos"
                azul
                icon={<SafetyCertificateOutlined />}
                onClick={() => this.validarPrenomina({ incluirSat: false })}
              >
                Verificar Datos
              </ButtonItem>
            ),
            puedeTimbre && (
              <ButtonItem
                key="ver-poliza"
                icon={<FileSearchOutlined />}
                onClick={() => this.abrirPreviaPoliza()}
              >
                {puedeVerPolizaContable ? 'Vista Previa de Póliza' : 'Ver Póliza'}
              </ButtonItem>
            ),
            // La póliza contable REAL, la que quedó guardada al timbrar. La de
            // arriba es la vista previa recalculada, que sólo existe antes de
            // timbrar; en una nómina parcialmente timbrada salen las dos.
            puedeVerPolizaContable && (
              <ButtonItem
                key="poliza-contable"
                icon={<FileSearchOutlined />}
                onClick={() => this.abrirPolizaContable()}
              >
                Ver Póliza Contable
              </ButtonItem>
            ),
            puedeTimbre && (
              <ButtonItem
                key="timbrar"
                verde={!timbradoDePrueba}
                ambar={timbradoDePrueba}
                icon={<SafetyCertificateOutlined />}
                onClick={() => this.setState({
                  modalCierreVisible: true,
                  fechaPagoModal: prenomina?.fechaPago ? moment(prenomina.fechaPago) : null,
                  fechaEmisionModal: fechaEmisionPorDefecto(),
                })}
                style={{ fontWeight: 600 }}
              >
                {timbradoDePrueba ? 'Timbrar de Prueba' : 'Timbrar Nómina'}
              </ButtonItem>
            ),
            // El reintento vivía sólo en la alerta del resultado del timbrado,
            // que desaparece al recargar la pantalla: con líneas en error y sin
            // esa alerta no había forma de retimbrar (y si TODAS fallaron
            // tampoco aparece "Timbrar Nómina", porque no quedan líneas listas).
            puedeReintentar && (
              <ButtonItem
                key="reintentar-timbrado"
                ambar
                icon={<SyncOutlined />}
                onClick={() => this.abrirReintentoTimbrado()}
              >
                Reintentar Timbrado ({lineasConError.length})
              </ButtonItem>
            ),
            puedeCancelar && (
              <ButtonItem
                key="cancelar-nomina"
                rojo
                icon={<CloseCircleOutlined />}
                onClick={() => this.setState({ cancelarModal: { visible: true, lineaId: null, nombreEmpleado: '' } })}
              >
                Cancelar Nómina ({lineasTimbradas.length})
              </ButtonItem>
            ),
            esHistorica && (
              <Tag key="historica" color="cyan" style={{ fontSize: 12, fontWeight: 600 }}>
                HISTÓRICA
              </Tag>
            ),
            puedeEliminarHistorica && (
              <ButtonItem
                key="eliminar-historica"
                blanco
                icon={<DeleteOutlined />}
                onClick={() => this.eliminarHistorico()}
              >
                Eliminar
              </ButtonItem>
            ),
            // Vista previa de la póliza antes de decidir si se marca la casilla al
            // aplicar el histórico (previsualizarPoliza acepta la histórica calculada).
            esHistorica && prenomina?.estatus === 'calculada' && (
              <ButtonItem
                key="ver-poliza-historica"
                icon={<FileSearchOutlined />}
                onClick={() => this.abrirPreviaPoliza()}
              >
                Ver Póliza
              </ButtonItem>
            ),
            puedeAplicarHistorico && (
              <ButtonItem
                key="aplicar-historico"
                verde
                icon={<HistoryOutlined />}
                style={{ fontWeight: 600 }}
                onClick={() => this.setState({ historicoModal: { visible: true, generarPoliza: false } })}
              >
                Aplicar Histórico
              </ButtonItem>
            ),
            puedeRevertirHistorico && (
              <ButtonItem
                key="revertir-historico"
                rojo
                icon={<RollbackOutlined />}
                onClick={() => this.revertirHistorico()}
              >
                Revertir Histórico
              </ButtonItem>
            ),
          ].filter(Boolean)}
        />

        {/* El panel de contenido: mismo borde y radio que el PageHeader de arriba, para
            que se lean como dos piezas de la misma pantalla y no como un bloque blanco
            suelto pegado a los lados. La sombra es apenas perceptible, sólo para
            despegarlo del fondo. */}
        <div
          style={{
            backgroundColor: 'white', padding: '0 20px 20px',
            border: '1px solid rgb(235,237,240)', borderRadius: 10,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          {/* `calculando`, `cerrando` y `cancelando` ya no prenden este Spin: de
              esos tres se encarga ModalProgreso, que además de bloquear la
              pantalla dice qué está pasando y en qué empleado va. El Spin a secas
              tapaba todo sin explicar nada. */}
          {/* En el paso 0 el Spin no entra: la bandeja trae su propio skeleton y un
              overlay encima nada más lo taparía. */}
          <Spin spinning={(loading && paso !== 0) || aplicandoHistorico}>
            {/* `key={paso}` remonta este div en cada cambio de paso, que es lo que
                vuelve a disparar la animación de entrada. Va aquí adentro y no en el
                panel blanco para no remontar el Spin en cada transición. */}
            <div key={paso} className="pn-paso">
            {paso === 1 && (
              <Steps current={1} size="small" style={{ padding: '16px 0 20px' }}>
                <Step title="Periodo"   description="Elegir periodo" />
                <Step title="Prenómina" description="Empleados y cálculo" />
              </Steps>
            )}

            {paso === 0 && (
              <BandejaPrenomina
                key={this.state.bandejaKey}
                onAbrirPrenomina={(id) => this.cargarPrenomina(id)}
                onAbrirOCrearPeriodo={(periodoId) => this.abrirOCrearPrenomina(periodoId)}
                onDatosIniciales={(d) => this.onDatosInicialesBandeja(d)}
                // null = el engrane ni se pinta. La pantalla de preferencias vive dentro
                // de Configuración Nómina, así que se gatea con el mismo permiso.
                onAbrirPreferencias={this.props.permisoConfiguracionNomina
                  ? () => this.setState({ preferenciasVisible: true })
                  : null}
                onImportarHistorica={() => this.setState({ paso: 2 })}
                onIrAPeriodos={() => this.props.history.push('/panel/periodos')}
                onIrATiposPeriodos={this.props.envios
                  ? () => this.props.history.push('/panel/tiposPeriodos')
                  : null}
              />
            )}

            {paso === 2 && (
              <ImportarNominaHistorica
                onCancelar={() => this.volverABandeja()}
                onCreada={(id) => this.cargarPrenomina(id)}
              />
            )}

            {/* Aviso permanente mientras se trabaja una histórica: es fácil olvidar
                que esta pantalla es la misma de la nómina viva y que aquí no se timbra. */}
            {esHistorica && (
              <Alert
                style={{ marginBottom: 12 }}
                type={historicaAplicada ? 'success' : 'info'}
                showIcon
                message={
                  historicaAplicada
                    ? 'Histórico aplicado — los acumulados de este periodo ya están cargados'
                    : 'Nómina histórica — pendiente de aplicar'
                }
                description={
                  historicaAplicada
                    ? <span>
                        Las líneas quedaron congeladas. Si necesitas corregir algún importe,
                        usa <b>Revertir Histórico</b>: los acumulados vuelven a su valor anterior
                        {prenomina?.polizaHistoricaGenerada ? ' y la póliza recibe los renglones espejo' : ''}.
                      </span>
                    : <span>
                        Este periodo ya se pagó en el sistema anterior: <b>no se timbra</b> y{' '}
                        <b>no mueve créditos de INFONAVIT, FONACOT ni movimientos permanentes</b>.
                        Presiona <b>Calcular</b> para que el motor derive el gravado y el exento de
                        cada concepto —los importes capturados no se mueven—, revisa la tabla y luego
                        <b> Aplicar Histórico</b>.
                      </span>
                }
              />
            )}

            {/* El avance de cálculo, timbrado, cancelación y validación ya no va
                aquí: vive en ModalProgreso, al final del render. Las barras
                inline se perdían detrás del scroll y quedaban tapadas por el Spin
                que envolvía toda la pantalla. */}

            {/* Resultado de la cancelación */}
            {paso === 1 && cancelacionResultado && !cancelando && (
              <Alert
                style={{ marginBottom: 12 }}
                type={cancelacionResultado.errores > 0 ? 'warning' : 'success'}
                showIcon
                closable
                onClose={() => this.setState({ cancelacionResultado: null })}
                message={
                  cancelacionResultado.errores > 0
                    ? `Cancelación parcial: ${cancelacionResultado.exitosos} CFDIs cancelados, ${cancelacionResultado.errores} con error`
                    : `${cancelacionResultado.exitosos} CFDI${cancelacionResultado.exitosos !== 1 ? 's' : ''} cancelado${cancelacionResultado.exitosos !== 1 ? 's' : ''} ante el SAT`
                }
              />
            )}

            {/* Resultado del cierre */}
            {paso === 1 && cierreResultado && !cerrando && (
              <Alert
                style={{ marginBottom: 12 }}
                type={cierreResultado.errores > 0 ? 'warning' : 'success'}
                showIcon
                message={
                  (cierreResultado.errores > 0
                    ? `Timbrado parcial: ${cierreResultado.exitosos} CFDIs generados, ${cierreResultado.errores} con error`
                    : `Nómina timbrada: ${cierreResultado.exitosos} CFDIs generados correctamente`)
                  // Los recibos en ceros no son error ni éxito: se saltaron a propósito.
                  // Sin este añadido los CFDIs generados no cuadran con los empleados de
                  // la nómina y parece que algo se perdió.
                  + (cierreResultado.sinTimbre > 0
                    ? `, ${cierreResultado.sinTimbre} sin timbre por venir en ceros`
                    : '')
                }
                action={
                  puedeReintentar && (
                    <ButtonItem
                      size="small"
                      icon={<SyncOutlined />}
                      // Arrow y no .bind: con bind el evento del click entraba
                      // como `lineaId` y el reintento habría salido filtrado.
                      onClick={() => this.abrirReintentoTimbrado()}
                    >
                      Reintentar ({lineasConError.length})
                    </ButtonItem>
                  )
                }
              />
            )}

            {/* Resultado del reset. Sólo aparece si entraron líneas nuevas y el usuario no pidió
                calcular: quedaron en Pendiente y saldrían en ceros hasta que se calculen. */}
            {paso === 1 && resetResultado && !calculando && (
              <Alert
                style={{ marginBottom: 12 }}
                type="info"
                showIcon
                closable
                onClose={() => this.setState({ resetResultado: null })}
                message={`${resetResultado.agregadas} línea(s) nueva(s) están en Pendiente y todavía no tienen importes.`}
                action={
                  <ButtonItem
                    size="small"
                    icon={<CalculatorOutlined />}
                    onClick={() => { this.setState({ resetResultado: null }); this.calcularTodo() }}
                  >
                    Calcular todo
                  </ButtonItem>
                }
              />
            )}

            {paso === 1 && (
              <TablaPrenomina
                prenomina={prenomina}
                conceptosNomina={conceptosNomina}
                tiposIncidencias={tiposIncidencias}
                // Preferencia de la empresa, reportada por la bandeja. Sólo la lee el
                // inicializador de la tabla: a este paso se llega siempre desde la bandeja.
                vistaInicial={this._vistaInicialTabla}
                // Los necesita la dispersión bancaria, que pide el catálogo de
                // bancos al dar clic en vez de cargarlo con la prenómina.
                empresaId={this.props.empresaId}
                headersToken={this.props.headersToken}
                calculando={calculando}
                cerrando={cerrando}
                cancelando={cancelando}
                onCalcularTodo={this.calcularTodo.bind(this)}
                onCalcularLinea={(id) => this.calcularLinea(id)}
                onQuitarEmpleado={(id) => this.quitarEmpleado(id)}
                onAbrirSobreRecibo={(linea) =>
                  this.setState({ sobreReciboVisible: true, empleadoIdSobreRecibo: linea.empleadoId })
                }
                onAbrirColumnas={() => this.setState({ columnasVisible: true })}
                onAgregarEmpleado={() => this.setState({ agregarEmpleadoVisible: true })}
                onResetearLineas={() => this.setState({ resetLineasVisible: true })}
                onGuardarIncidenciaDia={(empleadoId, diaPeriodo, fecha, entradas) =>
                  this.guardarIncidenciaDia(empleadoId, diaPeriodo, fecha, entradas)
                }
                onImportarIncidenciasAsistencia={(opciones) => this.importarIncidenciasAsistencia(opciones)}
                onImportarIncidenciasExcel={(payload) => this.importarIncidenciasExcel(payload)}
                onLimpiarIncidencias={() => this.limpiarIncidencias()}
                soloLectura={['timbrada', 'cerrada', 'parcialmente_timbrada', 'historica'].includes(prenomina?.estatus)}
                onAbrirCfdi={(linea) => this.abrirCfdi(linea)}
                onDescargarXml={(lineaId) => this.descargarXml(lineaId)}
                onImprimirRecibo={(lineaId) => this.imprimirRecibo(lineaId)}
                onValidacionParaReporte={() => this.validacionParaReporte()}
                onDescargarRecibos={(filtro) => this.abrirDescargaRecibos(filtro)}
                onVerRecibos={(lineas, filtro) => this.abrirVerRecibos(lineas, filtro)}
                onEnviarRecibos={(filtro) => this.abrirEnvioRecibos(filtro)}
                onReintentarLinea={(linea) => this.abrirReintentoTimbrado(linea._id)}
                puedeTimbrarLineaHistorica={
                  !!this.state.timbrarNominaHistorica
                  && !!prenomina?.esHistorica
                  && prenomina?.estatus === 'historica'
                }
                timbrandoLineaHistorica={this.state.timbrandoLineaHistorica}
                onTimbrarLineaHistorica={(linea) => this.timbrarLineaHistorica(linea)}
                onCancelarCfdi={(linea) => this.setState({
                  cancelarModal: {
                    visible: true,
                    lineaId: linea._id,
                    nombreEmpleado: [linea.nombre, linea.apellidoPaterno, linea.apellidoMaterno].filter(Boolean).join(' '),
                  },
                })}
              />
            )}
            </div>
          </Spin>
        </div>

        {/* Avance de los procesos largos: cálculo, validación con el SAT,
            timbrado y cancelación. Nunca corren dos a la vez, así que un solo
            modal los cubre todos. Es bloqueante a propósito: tocar la tabla a
            media corrida no lleva a nada bueno. */}
        <ModalProgreso
          visible={calculando || validandoSat || cerrando || cancelando}
          titulo={
            calculando   ? 'Calculando nómina'
            : validandoSat ? 'Validando ante el SAT'
            : cerrando     ? (timbradoDePrueba ? 'Timbrando nómina (pruebas)' : 'Timbrando nómina')
            :                'Cancelando CFDIs'
          }
          descripcion={
            calculando
              ? 'No cierres esta ventana: el cálculo sigue en el servidor, pero perderías el avance en pantalla.'
              : validandoSat
                ? 'Se consulta al validador del SAT empleado por empleado, con pausas para que no rechace por volumen.'
                : cerrando
                  ? 'Cada recibo se manda al PAC por separado. Los que fallen quedan marcados para reintentarlos después.'
                  : 'Cada CFDI se cancela ante el SAT por separado.'
          }
          procesados={
            calculando   ? progreso.procesados
            : validandoSat ? validacionProgreso.procesados
            : cerrando     ? cierreProgreso.procesados
            :                cancelacionProgreso.procesados
          }
          total={
            calculando   ? progreso.total
            : validandoSat ? validacionProgreso.total
            : cerrando     ? cierreProgreso.total
            :                cancelacionProgreso.total
          }
          exitosos={cerrando ? cierreProgreso.exitosos : cancelando ? cancelacionProgreso.exitosos : null}
          errores={
            cerrando ? cierreProgreso.errores
            : cancelando ? cancelacionProgreso.errores
            : validandoSat ? validacionProgreso.conProblemas
            : null
          }
          detalle={calculando && progreso.empleadoActual ? `Procesando: ${progreso.empleadoActual}` : null}
          colorBarra={cancelando ? '#cf1322' : undefined}
        />

        {/* Validación de datos antes de timbrar */}
        <ModalValidacionTimbrado
          visible={this.state.validacion.visible}
          loading={this.state.validacion.loading}
          resultados={this.state.validacion.resultados}
          titulo="Verificación de datos antes de timbrar"
          mensajeVacio="No hay líneas listas para timbrar en esta nómina."
          onCancel={() => this.setState({ validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false } })}
          onDescargarXml={(item) => this.descargarXmlPrueba(item)}
          onDescargarTodos={() => this.descargarTodosXmlPrueba()}
          footerExtra={
            !this.state.validacion.loading && this.state.validacion.resultados.length > 0 ? (
              <>
                <ButtonItem
                  azul
                  icon={<SafetyCertificateOutlined />}
                  onClick={() => this.validarPrenominaConSat()}
                >
                  Consultar el SAT
                </ButtonItem>
                {this.state.validacion.desdeTimbrado && (
                  <ButtonItem
                    ambar
                    icon={<SafetyCertificateOutlined />}
                    disabled={this.state.enviandoTimbrado}
                    loading={this.state.enviandoTimbrado}
                    onClick={() => {
                      this.setState({ validacion: { visible: false, loading: false, resultados: [], desdeTimbrado: false } })
                      this.confirmarCierre(true)
                    }}
                  >
                    Timbrar de todas formas
                  </ButtonItem>
                )}
              </>
            ) : null
          }
        />

        {/* Modal confirmación de cierre */}
        <Modal
          visible={modalCierreVisible}
          onCancel={() => this.setState({ modalCierreVisible: false })}
          title={
            <span style={{ color: timbradoDePrueba ? '#d46b08' : '#cf1322', fontWeight: 700, fontSize: 15 }}>
              <ExclamationCircleOutlined style={{ marginRight: 8 }} />
              {timbradoDePrueba
                ? 'Timbrar de Prueba — Generación de CFDIs'
                : 'Timbrar Nómina — Generación de CFDIs'}
            </span>
          }
          footer={[
            <ButtonItem key="cancel" onClick={() => this.setState({ modalCierreVisible: false })}>
              Cancelar
            </ButtonItem>,
            <ButtonItem
              key="confirm"
              rojo={!timbradoDePrueba}
              ambar={timbradoDePrueba}
              disabled={!fechaPagoModal || !fechaEmisionModal || this.state.enviandoTimbrado}
              loading={this.state.enviandoTimbrado}
              icon={<SafetyCertificateOutlined />}
              /* Sin la arrow, el evento del click entraría como `forzar` y se
                 saltaría la validación: un MouseEvent es truthy. */
              onClick={() => this.confirmarCierre(false)}
            >
              Sí, timbrar {lineasListas.length} CFDI{lineasListas.length !== 1 ? 's' : ''}
            </ButtonItem>,
          ]}
          width={560}
        >
          {timbradoDePrueba ? (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 6 }}>
              <b>Timbrado de prueba: los CFDIs NO son válidos ante el SAT.</b>
              <br />
              <span style={{ fontSize: 12, color: '#555' }}>
                La empresa tiene prendido el complemento "Timbrado de Prueba", así que
                cada recibo se envía al ambiente de pruebas del PAC. Aun así el sistema
                guarda los CFDIs, marca la nómina como timbrada y genera la póliza y los
                acumulados, igual que en producción.
              </span>
            </div>
          ) : (
            <div style={{ marginBottom: 16, padding: '12px 16px', background: '#fff1f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
              <b>Esta operación enviará los CFDIs de nómina al SAT y no se puede deshacer.</b>
              <br />
              <span style={{ fontSize: 12, color: '#555' }}>
                Al confirmar, el sistema timbra cada recibo ante el PAC configurado.
                El proceso corre en segundo plano y puede tardar varios minutos.
              </span>
            </div>
          )}

          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Periodo" span={2}>
              {prenomina?.nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha inicio">
              {prenomina?.fechaInicio ? moment(prenomina.fechaInicio).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha fin">
              {prenomina?.fechaFin ? moment(prenomina.fechaFin).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha de pago" span={2}>
              <div>
                <DatePicker
                  value={fechaPagoModal}
                  onChange={(date) => this.setState({ fechaPagoModal: date || null })}
                  format="DD/MM/YYYY"
                  style={{ width: '100%' }}
                  placeholder="Selecciona la fecha de pago"
                  status={!fechaPagoModal ? 'error' : ''}
                  disabledDate={(date) =>
                    prenomina?.fechaInicio && prenomina?.fechaFin &&
                    (date.isBefore(moment(prenomina.fechaInicio), 'day') || date.isAfter(moment(prenomina.fechaFin), 'day'))
                  }
                />
                {!fechaPagoModal && (
                  <div style={{ color: '#ff4d4f', fontSize: 11, marginTop: 3 }}>
                    Requerida para generar los CFDIs
                  </div>
                )}
                <div style={{ color: '#888', fontSize: 11, marginTop: 3 }}>
                  Debe estar dentro de los días del periodo ({prenomina?.fechaInicio ? moment(prenomina.fechaInicio).format('DD/MM/YYYY') : '—'} al {prenomina?.fechaFin ? moment(prenomina.fechaFin).format('DD/MM/YYYY') : '—'}).
                </div>
              </div>
            </Descriptions.Item>
            {/* Distinta de la fecha de pago: ésta es el atributo `Fecha` del
                comprobante. Por omisión es "ahora" y no hay que tocarla; sirve
                para timbrar un cierre que se corrió con un par de días de atraso. */}
            <Descriptions.Item label="Fecha de emisión del CFDI" span={2}>
              <SelectorFechaEmision
                value={fechaEmisionModal}
                onChange={(fecha) => this.setState({ fechaEmisionModal: fecha })}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Timbres a generar">
              <b style={{ fontSize: 16, color: '#1677ff' }}>{lineasListas.length}</b>
            </Descriptions.Item>
            <Descriptions.Item label="Neto total">
              <b>
                {netoTotalListas.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
              </b>
            </Descriptions.Item>
          </Descriptions>

          <Collapse ghost style={{ marginTop: 12 }}>
            <Collapse.Panel
              header={<span style={{ fontSize: 12, color: '#555' }}>Ver lista de empleados ({lineasListas.length})</span>}
              key="emp"
            >
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: 4 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#fafafa' }}>
                      <th style={{ textAlign: 'left', padding: '5px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>Empleado</th>
                      <th style={{ textAlign: 'right', padding: '5px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>Neto a pagar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineasListas.map((l, i) => (
                      <tr key={l.empleadoId} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                        <td style={{ padding: '4px 10px' }}>
                          {l.apellidoPaterno} {l.apellidoMaterno} {l.nombre}
                        </td>
                        <td style={{ padding: '4px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                          {netoLinea(l).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Collapse.Panel>
          </Collapse>

          <p style={{ marginTop: 12, marginBottom: 0, color: '#888', fontSize: 12 }}>
            ¿Confirmas el timbrado de esta nómina?
          </p>
        </Modal>

        {/* Modal timbrado de una línea de nómina histórica.
            El texto de advertencia es deliberadamente largo: este periodo ya se
            pagó en el sistema anterior y normalmente no se timbra, así que quien
            llega aquí tiene que saber qué va a pasar y qué NO va a pasar. */}
        <Modal
          visible={modalHistorica.visible}
          onCancel={() => this.setState({ modalHistorica: { visible: false, linea: null, fecha: null } })}
          title={
            <span>
              <ExclamationCircleOutlined style={{ color: '#faad14', marginRight: 8 }} />
              ¿Timbrar esta línea de la nómina histórica?
            </span>
          }
          footer={[
            <ButtonItem key="cancel" onClick={() => this.setState({ modalHistorica: { visible: false, linea: null, fecha: null } })}>
              Cancelar
            </ButtonItem>,
            <ButtonItem
              key="confirm"
              rojo
              icon={<SafetyCertificateOutlined />}
              disabled={!modalHistorica.fecha}
              onClick={() => this.confirmarTimbrarLineaHistorica()}
            >
              Timbrar de todos modos
            </ButtonItem>,
          ]}
          width={620}
        >
          <p style={{ marginBottom: 8 }}>
            Vas a emitir un <b>CFDI real ante el SAT</b> para{' '}
            <b>
              {[modalHistorica.linea?.nombre, modalHistorica.linea?.apellidoPaterno, modalHistorica.linea?.apellidoMaterno]
                .filter(Boolean).join(' ') || modalHistorica.linea?.serieNumero || 'este empleado'}
            </b>
            , y se va a <b>consumir un timbre</b>.
          </p>
          <p style={{ marginBottom: 8 }}>
            Este periodo <b>ya se pagó en el sistema anterior</b>: normalmente no se timbra.
            Hazlo sólo si a este recibo de verdad le falta su comprobante.
          </p>
          <p style={{ marginBottom: 8 }}>
            <b>Los acumulados no se van a mover</b> — ya los cargó el histórico. Sólo se genera
            el comprobante.
          </p>
          <p style={{ marginBottom: 8 }}>
            La línea <b>sale del histórico</b> y queda timbrada. Para deshacerlo hay que{' '}
            <b>cancelar el CFDI</b>, no revertir el histórico: mientras exista este comprobante,
            la reversa del histórico va a quedar bloqueada.
          </p>
          <p style={{ marginBottom: 12, color: '#d46b08' }}>
            Ojo: el SAT puede rechazar un periodo viejo por sus propias reglas de fecha.
          </p>

          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Fecha de emisión del CFDI">
              <SelectorFechaEmision
                value={modalHistorica.fecha}
                onChange={(fecha) => this.setState({ modalHistorica: { ...modalHistorica, fecha } })}
              />
            </Descriptions.Item>
          </Descriptions>
          <p style={{ marginTop: 8, marginBottom: 0, color: '#888', fontSize: 12 }}>
            Las fechas del periodo que se pagó (inicial, final y de pago) no cambian: sólo se
            mueve la fecha de emisión del comprobante.
          </p>
        </Modal>

        {/* Modal confirmación del reintento de timbrado. Antes era acción directa;
            ahora pide fecha de emisión porque el reintento genera CFDIs nuevos. */}
        <Modal
          visible={modalReintento.visible}
          onCancel={() => this.setState({ modalReintento: { visible: false, lineaId: null, fecha: null } })}
          title={<span><SyncOutlined style={{ marginRight: 8 }} />Reintentar Timbrado</span>}
          footer={[
            <ButtonItem key="cancel" onClick={() => this.setState({ modalReintento: { visible: false, lineaId: null, fecha: null } })}>
              Cancelar
            </ButtonItem>,
            <ButtonItem
              key="confirm"
              ambar
              icon={<SyncOutlined />}
              disabled={!modalReintento.fecha}
              onClick={() => this.reintentarTimbrado(modalReintento.lineaId, modalReintento.fecha)}
            >
              Reintentar
            </ButtonItem>,
          ]}
          width={520}
        >
          <p style={{ marginBottom: 12 }}>
            {modalReintento.lineaId
              ? 'Se va a volver a timbrar esa línea. '
              : `Se van a volver a timbrar las ${lineasConError.length} líneas que quedaron en error. `}
            Los datos del empleado se releen de la base, así que corregir el dato que rechazó el
            PAC y reintentar es suficiente.
          </p>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Fecha de emisión del CFDI">
              <SelectorFechaEmision
                value={modalReintento.fecha}
                onChange={(fecha) => this.setState({ modalReintento: { ...modalReintento, fecha } })}
              />
            </Descriptions.Item>
          </Descriptions>
        </Modal>

        {/* Modal detalle CFDI */}
        <Modal
          visible={cfdiModal.visible}
          onCancel={() => this.setState({ cfdiModal: { visible: false, loading: false, datos: null, lineaId: null, cfdis: [] } })}
          title={<span><SafetyCertificateOutlined style={{ color: '#52c41a', marginRight: 8 }} />Detalle CFDI de Nómina</span>}
          footer={[
            <ButtonItem
              key="xml"
              icon={<DownloadOutlined />}
              disabled={!cfdiModal.datos}
              onClick={() => this.descargarXml(cfdiModal.lineaId)}
            >
              Descargar XML
            </ButtonItem>,
            <ButtonItem key="close" onClick={() => this.setState({ cfdiModal: { visible: false, loading: false, datos: null, lineaId: null, cfdis: [] } })}>
              Cerrar
            </ButtonItem>,
          ]}
          width={480}
        >
          <Spin spinning={cfdiModal.loading}>
            {cfdiModal.datos && (
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="UUID">{cfdiModal.datos.uuid}</Descriptions.Item>
                {/* El segundo comprobante del mismo recibo: los pagos por separación se timbran
                    aparte, con régimen 13, porque el SAT no permite mezclarlos con los ordinarios. */}
                {(cfdiModal.cfdis || [])
                  .filter((c) => c.division === 'separacion' && c.uuid !== cfdiModal.datos.uuid)
                  .map((c) => (
                    <Descriptions.Item key={c.uuid} label="UUID separación">
                      {c.uuid}
                    </Descriptions.Item>
                  ))}
                <Descriptions.Item label="Fecha timbrado">
                  {cfdiModal.datos.fechaTimbrado ? moment(cfdiModal.datos.fechaTimbrado).format('DD/MM/YYYY HH:mm') : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="RFC PAC">{cfdiModal.datos.rfcPac}</Descriptions.Item>
                {cfdiModal.datos.noCertificadoSat && (
                  <Descriptions.Item label="No. Cert. SAT">{cfdiModal.datos.noCertificadoSat}</Descriptions.Item>
                )}
              </Descriptions>
            )}
          </Spin>
        </Modal>

        {/* Pólizas contables de la nómina timbrada. Sólo se pinta cuando hay más
            de una (póliza por empleado); con una sola, abrirPolizaContable salta
            directo al PDF. */}
        <ModalItem
          title="Pólizas de esta nómina"
          visible={this.state.polizasNomina.visible}
          spinning={this.state.polizasNomina.loading}
          onCancel={() => this.setState({ polizasNomina: { visible: false, loading: false, lista: [] } })}
          paddingTop
          width={800}
          footer={null}
        >
          <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
            Haz clic en una póliza para ver su PDF.
            {this.state.polizasNomina.lista.some((p) => p.esFiniquito) && (
              <>
                {' '}Cada <b>finiquito</b> se contabiliza en su propia póliza, aparte de la de la
                nómina del periodo.
              </>
            )}
          </div>
          <TableItem
            size="small"
            dataSource={this.state.polizasNomina.lista}
            rowKey="_id"
            defaultPageSize={10}
            onRow={(row) => ({
              style: { cursor: 'pointer' },
              onClick: () => this.setState({ polizaPdfId: row._id }),
            })}
            columns={[
              { title: 'Póliza', dataIndex: 'numeroTipoPoliza', key: 'numeroTipoPoliza', width: 90 },
              {
                title: 'Tipo',
                dataIndex: 'esFiniquito',
                key: 'esFiniquito',
                width: 100,
                render: (v) => (
                  v
                    ? <Tag color="purple" style={{ fontSize: 10 }}>Finiquito</Tag>
                    : <Tag style={{ fontSize: 10 }}>Nómina</Tag>
                ),
              },
              { title: 'Código', dataIndex: 'codigoEmpleado', key: 'codigoEmpleado', width: 100 },
              { title: 'Empleado', dataIndex: 'empleado', key: 'empleado' },
              {
                title: 'Importe',
                dataIndex: 'importe',
                key: 'importe',
                width: 130,
                align: 'right',
                render: (v) => dineroDisabledMask(v || 0),
              },
            ]}
          />
        </ModalItem>

        {/* PDF de una póliza. El componente pide polizas/datosPoliza y arma el
            documento él solo; aquí sólo se le dice cuál. */}
        <ModalPdfPoliza
          visible={!!this.state.polizaPdfId}
          polizaId={this.state.polizaPdfId}
          onCancel={() => this.setState({ polizaPdfId: null })}
        />

        {/* Modal vista previa de póliza — renderiza el PDF real (polizaPdf.js) con marca de agua "VISTA PREVIA" */}
        <Modal
          visible={polizaPreviewModal.visible}
          onCancel={() => this.setState({ polizaPreviewModal: { visible: false, loading: false, datos: null, empleadoIndex: 0 } })}
          title={<span><FileSearchOutlined style={{ color: '#1890ff', marginRight: 8 }} />Vista previa de la póliza de nómina</span>}
          footer={[
            <ButtonItem key="close" onClick={() => this.setState({ polizaPreviewModal: { visible: false, loading: false, datos: null, empleadoIndex: 0 } })}>
              Cerrar
            </ButtonItem>,
          ]}
          width={900}
        >
          <Spin spinning={polizaPreviewModal.loading}>
            {(() => {
              const { previews = [], advertencias = [] } = polizaPreviewModal.datos || {}
              return (
                <>
                  {advertencias.length > 0 && (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 12 }}
                      message="Advertencias"
                      description={
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {advertencias.map((a, i) => <li key={i}>{a}</li>)}
                        </ul>
                      }
                    />
                  )}
                  {previews.length > 1 && (
                    <SelectItem
                      style={{ width: '100%', marginBottom: 12 }}
                      value={polizaPreviewModal.empleadoIndex}
                      onChange={(idx) => this.cambiarEmpleadoPreviaPoliza(idx)}
                    >
                      {previews.map((p, idx) => (
                        <Option key={idx} value={idx}>{p.empleado?.nombre || 'General'}</Option>
                      ))}
                    </SelectItem>
                  )}
                  {/* El iframe se mantiene siempre montado (aunque aún no haya datos) para
                      que window.frames['polizaPreviewFrame'] ya exista cuando renderPreviaPoliza
                      llame pdf.open() justo después de que la petición resuelva. */}
                  <iframe title="polizaPreview" style={{ width: '100%', height: '65vh', border: 'none' }} id="polizaPreviewFrame" name="polizaPreviewFrame" />
                </>
              )
            })()}
          </Spin>
        </Modal>

        {/* Modal sobre-recibo */}
        {sobreReciboVisible && lineaSobreRecibo && (
          <SobreRecibo
            visible={sobreReciboVisible}
            linea={lineaSobreRecibo}
            prenomina={prenomina}
            tiposIncidencias={tiposIncidencias}
            conceptosNomina={conceptosNomina}
            empresaId={this.props.empresaId}
            empresaNombre={this.props.empresaNombre}
            headersToken={this.props.headersToken}
            onClose={() => this.setState({ sobreReciboVisible: false })}
            onEditarImporte={(conceptoNominaId, campo, valor) =>
              this.editarImporte(lineaSobreRecibo.empleadoId, conceptoNominaId, campo, valor)
            }
            onGuardarIncidenciaDia={(diaPeriodo, fecha, entradas) =>
              this.guardarIncidenciaDia(lineaSobreRecibo.empleadoId, diaPeriodo, fecha, entradas)
            }
            soloLectura={soloLecturaSobreRecibo}
            onRecalcularLinea={() => this.calcularLinea(lineaSobreRecibo.empleadoId)}
            onAgregarConcepto={(conceptoNominaId, importe) =>
              this.agregarConceptoManual(lineaSobreRecibo.empleadoId, conceptoNominaId, importe)
            }
            onQuitarConcepto={(conceptoNominaId) =>
              this.quitarConceptoManual(lineaSobreRecibo.empleadoId, conceptoNominaId)
            }
            onLimpiarManualConcepto={(conceptoNominaId) =>
              this.limpiarManualConcepto(lineaSobreRecibo.empleadoId, conceptoNominaId)
            }
            onCalcularNetoInverso={(netoDeseado) =>
              this.calcularNetoInverso(lineaSobreRecibo.empleadoId, netoDeseado)
            }
            onRevertirNetoInverso={() =>
              this.revertirNetoInverso(lineaSobreRecibo.empleadoId)
            }
          />
        )}

        {/* Modal columnas */}
        {columnasVisible && (
          <ModalColumnas
            visible={columnasVisible}
            conceptosNomina={conceptosNomina}
            columnasActuales={prenomina?.columnasVisibles || []}
            onClose={() => this.setState({ columnasVisible: false })}
            onGuardar={(cols) => this.guardarColumnas(cols)}
          />
        )}

        {/* Modal de preferencias — el atajo del engrane de la bandeja a la misma pantalla
            que vive en Configuración Nómina. Se configura donde duele. */}
        {this.state.preferenciasVisible && (
          <ModalItem
            title="Preferencias de nómina"
            visible
            spinning={false}
            width={720}
            onCancel={() => this.setState({ preferenciasVisible: false })}
          >
            <PreferenciasNomina
              anchoLibre
              // Subir bandejaKey la remonta, para que vuelva a pedir el sugerido con las
              // cadencias y los días de gracia recién guardados. Ese remonte NO re-dispara
              // el salto automático: `_autoEntradaHecha` ya está en true.
              onGuardado={() => this.setState((prev) => ({
                preferenciasVisible: false,
                bandejaKey: prev.bandejaKey + 1,
              }))}
            />
          </ModalItem>
        )}

        {/* Modal agregar empleado */}
        {agregarEmpleadoVisible && (
          <ModalAgregarEmpleado
            visible={agregarEmpleadoVisible}
            empleados={todosEmpleados}
            // Una histórica carga un periodo ya pagado: los empleados que desde
            // entonces se dieron de baja también tienen que poderse meter.
            incluirBajas={!!prenomina?.esHistorica}
            empleadosEnPrenomina={new Set((prenomina?.lineas || []).map(l => l.empleadoId))}
            onClose={() => this.setState({ agregarEmpleadoVisible: false })}
            onAgregar={(id) => this.agregarEmpleado(id)}
          />
        )}

        {/* Modal resetear líneas — resincroniza contra el padrón de empleados activos.
            Montaje condicional para que vuelva a pedir el preview cada vez que se abre: el
            padrón pudo moverse desde la última. */}
        {resetLineasVisible && (
          <ModalResetearLineas
            visible={resetLineasVisible}
            prenominaId={prenomina?._id}
            onClose={() => this.setState({ resetLineasVisible: false })}
            onAplicado={(r) => {
              this.setState({ resetLineasVisible: false })
              this.onResetAplicado(r)
            }}
          />
        )}

        {/* Modal recibo de nómina PDF + correo */}
        <ModalReciboNomina
          visible={reciboModal.visible}
          lineaId={reciboModal.lineaId}
          onClose={() => this.setState({ reciboModal: { visible: false, lineaId: null } })}
        />

        {/* Visor de todos los recibos en un PDF, para imprimirlos de una vez */}
        <ModalVerRecibos
          visible={verRecibosModal.visible}
          lineas={verRecibosModal.lineas}
          conceptosNomina={conceptosNomina}
          nombrePrenomina={this.state.prenomina?.nombre || 'nomina'}
          filtroFiniquito={verRecibosModal.filtroFiniquito}
          onClose={() => this.setState({ verRecibosModal: { visible: false, lineas: [], filtroFiniquito: 'todos' } })}
        />

        {/* Modal descarga masiva de recibos (XML/PDF/ambos) */}
        <ModalFormatoDescarga
          visible={descargaRecibosModal.visible}
          loading={descargaRecibosModal.loading}
          // Sin variante elegida (prenómina sin finiquitos) queda el título de siempre.
          titulo={etiquetaFiniquito(descargaRecibosModal.filtroFiniquito)
            ? `Descargar recibos — ${etiquetaFiniquito(descargaRecibosModal.filtroFiniquito)}`
            : undefined}
          onClose={() => this.setState({ descargaRecibosModal: { visible: false, loading: false } })}
          onConfirm={(formato, soloTimbrados) => this.confirmarDescargaRecibos(formato, soloTimbrados)}
          opcionUnPdf
          opcionSoloTimbrados
        />

        {/* Modal envío masivo de recibos por correo */}
        <ModalEnviarRecibos
          visible={enviarRecibosModal.visible}
          lineaIds={enviarRecibosModal.lineaIds}
          onClose={() => this.setState({ enviarRecibosModal: { visible: false, lineaIds: [] } })}
        />

        {/* Modal cancelar CFDI — un recibo (lineaId) o la nómina completa (null) */}
        <CancelarCfdiNomina
          visible={cancelarModal.visible}
          lineaId={cancelarModal.lineaId}
          nombreEmpleado={cancelarModal.nombreEmpleado}
          prenominaId={prenomina?._id}
          totalTimbradas={lineasTimbradas.length}
          cerrar={() => this.setState({ cancelarModal: { visible: false, lineaId: null, nombreEmpleado: '' } })}
          onCancelado={() => {
            this.setState({ cancelarModal: { visible: false, lineaId: null, nombreEmpleado: '' } })
            this.cargarPrenomina(prenomina._id)
          }}
          onCancelacionIniciada={(total) => this.setState({
            cancelarModal: { visible: false, lineaId: null, nombreEmpleado: '' },
            cancelando: true,
            cancelacionResultado: null,
            cancelacionProgreso: { procesados: 0, total, exitosos: 0, errores: 0 },
          }, () => this.iniciarRespaldo('cierre'))}
        />

        {/* Modal confirmación — aplicar el histórico a los acumulados */}
        <Modal
          visible={historicoModal.visible}
          onCancel={() => this.setState({ historicoModal: { ...historicoModal, visible: false } })}
          width={620}
          title={
            <span style={{ color: '#08979c', fontWeight: 700, fontSize: 15 }}>
              <HistoryOutlined style={{ marginRight: 8 }} />
              Aplicar Histórico — Acumulados del ejercicio
            </span>
          }
          footer={[
            <ButtonItem key="cancel" onClick={() => this.setState({ historicoModal: { ...historicoModal, visible: false } })}>
              Cancelar
            </ButtonItem>,
            <ButtonItem
              key="confirm"
              verde
              loading={aplicandoHistorico}
              onClick={() => this.aplicarHistorico()}
            >
              Aplicar Histórico
            </ButtonItem>,
          ]}
        >
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Periodo">{prenomina?.nombre}</Descriptions.Item>
            <Descriptions.Item label="Empleados">{prenomina?.lineas?.length || 0}</Descriptions.Item>
            <Descriptions.Item label="Total percepciones">
              ${(prenomina?.totalPercepciones || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </Descriptions.Item>
            <Descriptions.Item label="Total deducciones">
              ${(prenomina?.totalDeducciones || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </Descriptions.Item>
            <Descriptions.Item label="Neto">
              ${(prenomina?.totalNeto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
            </Descriptions.Item>
          </Descriptions>

          <Checkbox
            checked={historicoModal.generarPoliza}
            onChange={(e) => this.setState({ historicoModal: { ...historicoModal, generarPoliza: e.target.checked } })}
          >
            Generar también la póliza contable de esta nómina
          </Checkbox>
          <div style={{ fontSize: 12, color: '#888', margin: '4px 0 0 24px' }}>
            Marca esto solo si la contabilidad de este periodo todavía no está registrada en Biwo.
            Si la póliza falla por configuración contable, los acumulados igual quedan aplicados.
          </div>

          <Alert
            style={{ marginTop: 16 }}
            type="warning"
            showIcon
            message="Esto NO timbra ni mueve créditos"
            description={
              <span>
                Se van a sumar los acumulados de {prenomina?.mes ? `${prenomina.mes}/` : ''}{prenomina?.ejercicio} de cada
                empleado. No se emite ningún CFDI, no se consumen timbres, y no se toca el saldo de
                INFONAVIT, FONACOT, movimientos permanentes ni el ajuste al neto. Se puede deshacer
                con <b>Revertir Histórico</b>.
              </span>
            }
          />
        </Modal>
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken:  state.user.headersToken,
  empresaId:     state.empresa.id,
  // Encabezado del PDF y del Excel de la memoria de cálculo del sobre-recibo.
  empresaNombre: state.empresa.nombre,
  usuarioId:     state.user.id,
  // La ruta /panel/tiposPeriodos sólo existe con este complemento prendido, así que
  // la bandeja sólo ofrece ese atajo cuando de verdad lleva a algún lado.
  envios:        state.empresa.envios,
  // El mismo permiso que gatea la pantalla de Configuración Nómina, donde vive la
  // pestaña de preferencias (ver Panel.js). El engrane de la bandeja es un atajo a esa
  // misma pantalla, así que no puede abrir una puerta que el menú tiene cerrada.
  permisoConfiguracionNomina:
    state.user.tipo === 0 ||
    state.user.permisos.find(
      (valueTodos) => valueTodos.permisoId.toString() === '6a96045e6102bd5076d2a3f8'
    )?.activado,
})

export default connect(mapStateToProps)(AgregarPrenomina)